import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ItemSuggestions from '../../components/items/ItemSuggestions';
import ListingPhotosGuidance from '../../components/items/ListingPhotosGuidance';
import ListingItemFlawsField from '../../components/items/ListingItemFlawsField';
import WeddingEssentialsPolicy from '../../components/items/WeddingEssentialsPolicy';
import LocationPicker from '../../components/common/LocationPicker';
import ImageCropModal from '../../components/common/ImageCropModal';
import PhotoSlotGrid from '../../components/items/PhotoSlotGrid';
import { authAPI } from '../../api/authAPI';
import { resolveIsLuxuryListing } from '../../utils/luxuryPriceGate';
import { sanitizeDecimalInput } from '../../utils/sanitizeText';
import {
  trackListingCreateStarted,
  trackListingCreateSucceeded,
  trackListingCreateFailed,
} from '../../utils/analytics';
import { requestIdFromError } from '../../utils/requestId';
import { itemsAPI } from '../../api/itemsAPI';
import { cancelPendingUpload, isAbortError, uploadPendingImage } from '../../utils/pendingImageUploads';
import {
  EVERYDAY_CLOTHING_SECTION,
  SOIREE_CLOTHING_SECTION,
  WEDDING_PRE_LOVED_CATEGORY_DATA,
  apiCategoryForClothingSection,
  CLOTHING_OCCASION_SECTION_NAMES,
  getWeddingCategoryDepartment,
  isOccasionClothingSection,
  isWeddingListingCategory,
} from '../../constants/weddingCategories';
import {
  getListingSizeColorAttributes,
  buildSizeColorPayload,
} from '../../constants/listingSizeColorAttributes';
import {
  WEDDING_DRESS_OPTIONS,
  EMPTY_WEDDING_DRESS_FORM,
  buildWeddingDressJson,
  getWeddingDressColorOptions,
} from '../../constants/weddingDressSchema';
import {
  GROOM_STRUCTURED_SUBCATEGORIES,
  GROOM_SUIT_OPTIONS,
  EMPTY_GROOM_SUIT_FORM,
  buildGroomSuitJson,
} from '../../constants/weddingGroomSchema';
import { PRE_OWNED_ITEM_CATEGORY_DATA, LUXURY_ITEM_CATEGORIES } from '../../constants/preOwnedItemCategories';
import { HOBBIES_FINDS_ITEM_CATEGORIES } from '../../constants/hobbiesFindsCategories';
import {
  CLOTHING_AUDIENCES,
  getClothingTypes,
  getClothingSubtypes,
  getClothingStyles,
  isClothingListingCategory,
  joinClothingSubcategoryPath,
} from '../../constants/clothingCategorySchema';
import {
  PET_TYPES,
  PET_SUPPLIES_SECTIONS,
  getPetGroups,
  getPetProducts,
  isPetSuppliesListingCategory,
  joinPetSubcategoryPath,
} from '../../constants/petSuppliesCategorySchema';
import {
  BOOK_TYPES,
  BOOK_LANGUAGES,
  BOOK_AUDIENCES,
  BOOK_SECTIONS,
  getBookGroups,
  getBookTopics,
  isBooksListingCategory,
  joinBookSubcategoryPath,
} from '../../constants/booksCategorySchema';
import {
  isElectronicsListingCategory,
} from '../../constants/contactOnlyCategories';
import {
  KITCHEN_DINING_GROUPS,
  KITCHEN_DINING_ROOT,
  getKitchenDiningTypes,
  isHomeKitchenListingCategory,
  isKitchenDiningSubcategory,
  joinKitchenDiningSubcategoryPath,
} from '../../constants/kitchenDiningCategorySchema';
import {
  WATCH_DEPARTMENTS,
  getWatchSubcategories,
  isWatchesListingCategory,
  joinWatchSubcategoryPath,
} from '../../constants/watchesCategorySchema';
import {
  BAG_DEPARTMENTS,
  getBagSubcategories,
  isBagsListingCategory,
  joinBagSubcategoryPath,
} from '../../constants/bagsCategorySchema';
import {
  FOOTWEAR_DEPARTMENTS,
  getFootwearSubcategories,
  isFootwearListingCategory,
  joinFootwearSubcategoryPath,
} from '../../constants/footwearCategorySchema';
import {
  ACCESSORY_DEPARTMENTS,
  getAccessorySubcategories,
  isAccessoriesListingCategory,
  joinAccessorySubcategoryPath,
} from '../../constants/accessoriesCategorySchema';
import styled from 'styled-components';
import ListingTypeSelector from '../../components/items/ListingTypeSelector';
import BrandSelector from '../../components/items/BrandSelector';
import {
  LISTING_TYPE_OPTIONS,
  looksLikeWeddingOccasionClothing,
  resolveListingTypeId,
} from '../../constants/listingTypeOptions';
import {
  EMPTY_BRAND_SELECTION,
  appendBrandFields,
  brandSelectionError,
} from '../../api/brandsAPI';
import { getTaxonomyResetPatch } from '../../constants/listingCategoryTaxonomy';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f6f1e8;
  padding: 2rem 1rem 4rem;
`;

const PageInner = styled.div`
  max-width: 720px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 1.5rem;
`;

const PageTitle = styled.h1`
  font-size: ${(p) => (p.$elegant ? '1.4rem' : '1.3rem')};
  font-weight: ${(p) => (p.$elegant ? '600' : '700')};
  font-family: ${(p) =>
    p.$elegant ? "'Playfair Display', Georgia, 'Times New Roman', serif" : 'inherit'};
  letter-spacing: ${(p) => (p.$elegant ? '0.01em' : 'normal')};
  line-height: ${(p) => (p.$elegant ? '1.25' : '1.3')};
  color: #1e293b;
  margin-bottom: ${(p) => (p.$elegant ? '0' : '0.25rem')};
`;

const PageSubtitle = styled.p`
  font-size: 0.95rem;
  color: #334155;
  line-height: 1.55;
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const InlineChoiceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const InlineChoiceOptions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  border-radius: 0.5rem;
  border: 1.5px solid ${props => props.$active ? '#10b981' : '#e5e7eb'};
  background-color: ${props => props.$active ? '#ecfdf5' : '#ffffff'};
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${props => props.$active ? '#065f46' : '#4b5563'};

  &:hover {
    border-color: #10b981;
    background-color: #f0fdf4;
  }

  input[type="radio"] {
    accent-color: #10b981;
    width: 1rem;
    height: 1rem;
  }
`;

const ChipOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  border-radius: 0.5rem;
  border: 1.5px solid ${props => props.$active ? '#10b981' : '#e5e7eb'};
  background-color: ${props => props.$active ? '#ecfdf5' : '#ffffff'};
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${props => props.$active ? '#065f46' : '#4b5563'};

  &:hover {
    border-color: #10b981;
    background-color: #f0fdf4;
  }

  input[type="checkbox"] {
    accent-color: #10b981;
    width: 1rem;
    height: 1rem;
  }
`;

const AttributeHint = styled.p`
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin: 0 0 0.75rem 0;
  line-height: 1.45;
`;

const SizeGroupBlock = styled.div`
  margin-bottom: 1rem;
`;

const SizeGroupLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  margin-bottom: 0.5rem;
`;

const ChipGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const ColorDot = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  background: ${(props) => props.$color || '#e5e7eb'};
  flex-shrink: 0;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  font-size: 1rem;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
  
  &[type="file"] {
    padding: 0.5rem;
    cursor: pointer;
  }
  
  ${props => props.error && `
    border-color: #dc3545;
  `}
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  font-size: 1rem;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  font-family: inherit;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
  
  ${props => props.error && `
    border-color: #dc3545;
  `}
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  font-size: 1rem;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  cursor: pointer;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
  
  ${props => props.error && `
    border-color: #dc3545;
  `}
`;

const ErrorText = styled.span.attrs({ className: 'field-error-text' })`
  color: #dc3545;
  font-size: 0.875rem;
`;

const ListingHint = styled.div`
  margin: 0 0 1rem;
  padding: 0.85rem 1rem;
  border-radius: 0.75rem;
  border: 1.5px solid #f9a8d4;
  background: #fff7fb;
  color: #9d174d;
  font-size: 0.9rem;
  line-height: 1.45;
`;

const ListingHintButton = styled.button`
  display: inline-block;
  margin-top: 0.55rem;
  padding: 0.45rem 0.85rem;
  border: none;
  border-radius: 999px;
  background: #be185d;
  color: #fff;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #9d174d;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: var(--primary-color);
  color: white;
  
  &:hover:not(:disabled) {
    background-color: var(--primary-dark);
  }
`;

const SecondaryButton = styled(Button)`
  background-color: transparent;
  color: var(--text-primary);
  border: 2px solid var(--border-color);
  
  &:hover:not(:disabled) {
    background-color: var(--bg-secondary);
  }
`;

const ImageOrderHint = styled.p`
  margin: 0.75rem 0 0;
  color: #475569;
  font-size: 0.82rem;
  line-height: 1.45;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const CreateItemPage = ({
  weddingPreLovedMode = false,
  preferredCategoryGroup = '',
  luxuryPieceMode = false,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const weddingEntryMode =
    preferredCategoryGroup === 'wedding'
    || weddingPreLovedMode
    || searchParams.get('group') === 'wedding';
  const hobbiesEntryMode =
    preferredCategoryGroup === 'hobbies'
    || searchParams.get('group') === 'hobbies';
  const defaultDefected =
    !weddingEntryMode &&
    !hobbiesEntryMode &&
    !luxuryPieceMode &&
    (searchParams.get('defected') === '1' || searchParams.get('defected') === 'true');
  const { loading } = useSelector((state) => state.items);
  const { user } = useSelector((state) => state.auth);

  // Check for suggested item from WhatToOfferPage
  const suggestedItem = typeof window !== 'undefined' 
    ? sessionStorage.getItem('suggestedItem') 
    : null;

  const [formData, setFormData] = useState({
    title: suggestedItem || '',
    description: '',
    price: '',
    category: weddingEntryMode ? 'Clothing' : '',
    subcategory: '',
    book_subcategory: '',
    watch_department: '',
    watch_subcategory: '',
    bag_department: '',
    bag_subcategory: '',
    footwear_department: '',
    footwear_subcategory: '',
    accessory_department: '',
    accessory_subcategory: '',
    clothing_audience: '',
    clothing_type: '',
    clothing_subtype: '',
    clothing_style: '',
    pet_type: '',
    pet_section: '',
    pet_group: '',
    pet_product: '',
    book_section: '',
    book_group: '',
    book_topic: '',
    book_type: '',
    book_language: '',
    book_audience: '',
    kitchen_group: '',
    kitchen_type: '',
    phone_discoverable: '',
    condition: weddingEntryMode ? 'Used' : '',
    in_stock: '1',
    weight_over_15kg: false,
    fragile: false,
    is_anonymous: 'false',
    is_defected: defaultDefected ? 'true' : 'false',
    has_receipt_or_guarantee: '',
    is_hijabi: '',
    color: '',
  });

  const isDefectedSelected = defaultDefected || formData.is_defected === 'true';
  const askReceiptOrGuarantee = !isDefectedSelected;
  const brandRequired = !isDefectedSelected;

  const [selectedSizes, setSelectedSizes] = useState([]);
  const [weddingDress, setWeddingDress] = useState(EMPTY_WEDDING_DRESS_FORM);
  const [groomSuit, setGroomSuit] = useState(EMPTY_GROOM_SUIT_FORM);
  const [clothingListingSection, setClothingListingSection] = useState('');

  // Clear suggested item after using it
  React.useEffect(() => {
    if (suggestedItem && typeof window !== 'undefined') {
      sessionStorage.removeItem('suggestedItem');
    }
  }, [suggestedItem]);

  const [phoneDiscoverConfirm, setPhoneDiscoverConfirm] = useState(false);
  const [showAddressStep, setShowAddressStep] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedImages, setSelectedImages] = useState([]);
  const photoInputRef = useRef(null);
  const [cropModal, setCropModal] = useState(null); // { index, src, fileName } | null
  const [cropQueue, setCropQueue] = useState([]);
  const [itemFlaws, setItemFlaws] = useState([]);
  const [brandSelection, setBrandSelection] = useState(EMPTY_BRAND_SELECTION);
  const [uploading, setUploading] = useState(false);
  const [waitingUploads, setWaitingUploads] = useState(false);
  const imageIdRef = useRef(0);
  const selectedImagesRef = useRef([]);
  const uploadControllersRef = useRef(new Map()); // imageId -> AbortController
  const uploadPromisesRef = useRef(new Map()); // imageId -> Promise
  React.useEffect(() => {
    selectedImagesRef.current = selectedImages;
  }, [selectedImages]);
  const [savingAddress, setSavingAddress] = useState(false);
  const [shippingAddress, setShippingAddress] = useState(user?.shipping_address || '');
  const [addressCoords, setAddressCoords] = useState({
    latitude: user?.latitude ?? null,
    longitude: user?.longitude ?? null,
  });
  React.useEffect(() => {
    const initialAddress = user?.shipping_address || '';
    setShippingAddress(initialAddress);
    setAddressCoords({
      latitude: user?.latitude ?? null,
      longitude: user?.longitude ?? null,
    });
  }, [user]);

  const categoryData = PRE_OWNED_ITEM_CATEGORY_DATA;

  const isWeddingListing = isWeddingListingCategory(formData.category);
  const showWeddingContext =
    isWeddingListing || isOccasionClothingSection(clothingListingSection);
  const showDefectedQuestion = !showWeddingContext && !luxuryPieceMode;
  const categories = luxuryPieceMode
    ? LUXURY_ITEM_CATEGORIES
    : hobbiesEntryMode
      ? HOBBIES_FINDS_ITEM_CATEGORIES
      : Object.keys(categoryData);

  const getSubcategories = (category) => {
    if (isWeddingListingCategory(category)) {
      return WEDDING_PRE_LOVED_CATEGORY_DATA[category] || [];
    }
    // Luxury listings use the same category / subcategory tree as pre-owned.
    return categoryData[category] || [];
  };

  const exitPath = isWeddingListing
    ? '/wedding-essentials'
    : hobbiesEntryMode
      ? '/hobbies-finds'
      : luxuryPieceMode
        ? '/luxury'
        : '/pre-owned-items';

  const listingAttrs = getListingSizeColorAttributes(formData.category);
  const BRIDAL_STRUCTURED_SUBCATEGORIES = [
    'Wedding Dresses',
    'Engagement Dresses',
    'Soiree Dresses',
  ];
  const COLOR_SWATCH_MAP = {
    White: '#ffffff',
    Ivory: '#fffaf0',
    'Off-white': '#f8f8f2',
    Champagne: '#f7e7ce',
    Gold: '#d4af37',
    Silver: '#c0c0c0',
    Black: '#111827',
    Navy: '#1e3a8a',
    Red: '#dc2626',
    Burgundy: '#7f1d1d',
    Emerald: '#047857',
    Purple: '#7c3aed',
    'Rose Gold': '#b76e79',
    'Blush Pink': '#fbcfe8',
    Rose: '#f43f5e',
    'Dusty Rose': '#d8a7b1',
    Mauve: '#b784a7',
    Nude: '#d6b89c',
    Lilac: '#c4b5fd',
    'Sky Blue': '#7dd3fc',
    'Sage Green': '#86efac',
  };
  const isWeddingDressListing =
    isWeddingListing &&
    formData.category === 'Bridal Gowns and Essentials' &&
    BRIDAL_STRUCTURED_SUBCATEGORIES.includes(formData.subcategory);
  const isGroomStructuredListing =
    isWeddingListing &&
    formData.category === 'Groom Suits & Essentials' &&
    GROOM_STRUCTURED_SUBCATEGORIES.includes(formData.subcategory);
  const isStructuredWeddingFlow = isWeddingDressListing || isGroomStructuredListing;
  const dressColorOptions = getWeddingDressColorOptions(formData.subcategory);

  const clearWeddingAttributeSelections = () => {
    setFormData((prev) => ({ ...prev, color: '' }));
    setSelectedSizes([]);
    setWeddingDress(EMPTY_WEDDING_DRESS_FORM);
    setGroomSuit(EMPTY_GROOM_SUIT_FORM);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.color;
      delete next.sizes;
      Object.keys(next).forEach((k) => {
        if (k.startsWith('dress_') || k.startsWith('groom_')) delete next[k];
      });
      return next;
    });
  };

  const switchToWeddingSection = (section = SOIREE_CLOTHING_SECTION) => {
    const occasionCategory = apiCategoryForClothingSection(section);
    setFormData((prev) => ({
      ...prev,
      ...getTaxonomyResetPatch(),
      category: occasionCategory || 'Clothing',
      subcategory: '',
      condition: prev.condition || 'Used',
      is_defected: 'false',
    }));
    setClothingListingSection(section);
    clearWeddingAttributeSelections();
    setErrors((prev) => ({
      ...prev,
      clothing_listing_section: '',
      wedding_category: '',
      subcategory: '',
    }));
  };

  const currentListingType = resolveListingTypeId({
    weddingPreLovedMode: showWeddingContext,
    luxuryPieceMode,
    defectedDefault: defaultDefected,
    hobbiesEntryMode,
  });
  const handleListingTypeChange = (typeId) => {
    if (typeId === currentListingType) return;
    if (typeId === 'wedding') {
      switchToWeddingSection();
      return;
    }
    if (typeId === 'usable' && (showWeddingContext || hobbiesEntryMode)) {
      if (hobbiesEntryMode) {
        navigate('/create-item');
        return;
      }
      setFormData((prev) => ({
        ...prev,
        ...getTaxonomyResetPatch(),
        category: 'Clothing',
        is_defected: 'false',
      }));
      setClothingListingSection(EVERYDAY_CLOTHING_SECTION);
      clearWeddingAttributeSelections();
      return;
    }
    const option = LISTING_TYPE_OPTIONS.find((entry) => entry.id === typeId);
    if (option) navigate(option.path);
  };

  const updateWeddingDress = (field, value) => {
    setWeddingDress((prev) => ({ ...prev, [field]: value }));
    const key = `dress_${field}`;
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const toggleWeddingDressMulti = (field, value) => {
    setWeddingDress((prev) => {
      const current = Array.isArray(prev[field]) ? prev[field] : [];
      const next = current.includes(value)
        ? current.filter((x) => x !== value)
        : [...current, value];
      return { ...prev, [field]: next };
    });
    const key = `dress_${field}`;
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };
  const updateGroomSuit = (field, value) => {
    setGroomSuit((prev) => ({ ...prev, [field]: value }));
    const key = `groom_${field}`;
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };
  const toggleGroomSuitMulti = (field, value) => {
    setGroomSuit((prev) => {
      const current = Array.isArray(prev[field]) ? prev[field] : [];
      const next = current.includes(value)
        ? current.filter((x) => x !== value)
        : [...current, value];
      return { ...prev, [field]: next };
    });
    const key = `groom_${field}`;
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const conditions = [
    'Excellent',
    'New',
    'Like New',
    'Good',
    'Fair',
    'Used',
    'Poor',
  ];

  const validateForm = () => {
    const newErrors = {};

    // Title validation
    if (!formData.title.trim()) {
      newErrors.title = t('listing.titleRequired');
    } else if (formData.title.trim().length < 3) {
      newErrors.title = t('listing.titleTooShort');
    } else if (formData.title.trim().length > 200) {
      newErrors.title = t('listing.titleTooLong');
    }

    // Description validation
    if (!formData.description.trim()) {
      newErrors.description = t('listing.descriptionRequired');
    } else if (formData.description.trim().length < 25) {
      newErrors.description = t('listing.descriptionTooShort');
    } else if (formData.description.trim().length > 5000) {
      newErrors.description = t('listing.descriptionTooLong');
    }

    // Price validation (skip wedding structured flow — sale price is validated there)
    if (!isStructuredWeddingFlow) {
      if (!formData.price.trim()) {
        newErrors.price = t('listing.priceRequired');
      } else {
        const price = parseFloat(formData.price);
        if (isNaN(price) || price <= 0) {
          newErrors.price = t('listing.priceInvalid');
        }
      }
    }

    // Category validation
    if (!formData.category) {
      newErrors.category = t('listing.categoryRequired');
    }
    const isClothingRoot = formData.category === 'Clothing' || isWeddingListing;
    if (isClothingRoot && !clothingListingSection) {
      newErrors.clothing_listing_section = t('listing.clothingSectionRequired');
    }
    if (isOccasionClothingSection(clothingListingSection) && !isWeddingListing) {
      newErrors.clothing_listing_section = t('listing.clothingSectionRequired');
    }

    // Subcategory validation (required when category has subcategories)
    if (formData.category && getSubcategories(formData.category).length > 0 && !formData.subcategory) {
      newErrors.subcategory = t('listing.subcategoryRequired');
    }

    if (isWatchesListingCategory(formData.category)) {
      if (!formData.watch_department) newErrors.watch_department = t('listing.departmentRequired');
      if (!formData.watch_subcategory) newErrors.watch_subcategory = t('listing.subcategoryRequired');
    }

    if (isBagsListingCategory(formData.category)) {
      if (!formData.bag_department) newErrors.bag_department = t('listing.departmentRequired');
      if (!formData.bag_subcategory) newErrors.bag_subcategory = t('listing.subcategoryRequired');
    }

    if (isFootwearListingCategory(formData.category)) {
      if (!formData.footwear_department) newErrors.footwear_department = t('listing.departmentRequired');
      if (!formData.footwear_subcategory) newErrors.footwear_subcategory = t('listing.subcategoryRequired');
    }

    if (isAccessoriesListingCategory(formData.category)) {
      if (!formData.accessory_department) newErrors.accessory_department = t('listing.departmentRequired');
      if (!formData.accessory_subcategory) newErrors.accessory_subcategory = t('listing.subcategoryRequired');
    }

    if (
      formData.category === 'Clothing'
      && clothingListingSection === EVERYDAY_CLOTHING_SECTION
    ) {
      if (!formData.clothing_audience) newErrors.clothing_audience = t('listing.departmentRequired');
      if (!formData.clothing_type) newErrors.clothing_type = t('listing.clothingTypeRequired');
      if (!formData.clothing_subtype) newErrors.clothing_subtype = t('listing.subtypeRequired');
      if (!formData.clothing_style) newErrors.clothing_style = t('listing.styleRequired');
    }

    if (isPetSuppliesListingCategory(formData.category)) {
      if (!formData.pet_type) newErrors.pet_type = t('listing.petTypeRequired');
      if (!formData.pet_section) newErrors.pet_section = t('listing.selectCategoryRequired');
      if (!formData.pet_group) newErrors.pet_group = t('listing.subcategoryRequired');
      if (!formData.pet_product) newErrors.pet_product = t('listing.productTypeRequired');
    }

    if (isBooksListingCategory(formData.category)) {
      if (!formData.book_type) newErrors.book_type = t('listing.bookTypeRequired');
      if (!formData.book_language) newErrors.book_language = t('listing.languageRequired');
      if (!formData.book_audience) newErrors.book_audience = t('listing.audienceRequired');
      if (!formData.book_section) newErrors.book_section = t('listing.subjectAreaRequired');
      if (!formData.book_group) newErrors.book_group = t('listing.subcategoryRequired');
      if (!formData.book_topic) newErrors.book_topic = t('listing.topicRequired');
    }

    if (isElectronicsListingCategory(formData.category)) {
      if (!(user?.phone_number || '').trim()) {
        newErrors.phone_discoverable = t('listing.phoneRequiredForElectronics');
      } else if (!phoneDiscoverConfirm) {
        newErrors.phone_discoverable = t('listing.phoneConfirmRequired');
      }
    }

    if (
      isHomeKitchenListingCategory(formData.category) &&
      isKitchenDiningSubcategory(formData.subcategory)
    ) {
      if (!formData.kitchen_group) newErrors.kitchen_group = t('listing.kitchenCategoryRequired');
      if (!formData.kitchen_type) newErrors.kitchen_type = t('listing.kitchenTypeRequired');
    }

    const brandErr = brandSelectionError(brandSelection, { required: brandRequired });
    if (brandErr) newErrors.brand = brandErr;

    // Condition validation
    if (!formData.condition) {
      newErrors.condition = t('listing.conditionRequired');
    }

    if (formData.is_anonymous === '') {
      newErrors.is_anonymous = t('listing.postingPrivacyRequired');
    }

    if (askReceiptOrGuarantee && !formData.has_receipt_or_guarantee) {
      newErrors.has_receipt_or_guarantee = t('listing.receiptRequired');
    }

    if (
      !isStructuredWeddingFlow
      && listingAttrs
      && (!showWeddingContext || isWeddingListing)
    ) {
      if (!formData.color) {
        newErrors.color = t('listing.colorRequired');
      }
      if (listingAttrs.requiresSize && selectedSizes.length === 0) {
        newErrors.sizes = t('listing.sizesRequired');
      }
    }

    if (isWeddingDressListing) {
      const requiredDressFields = [
        ['silhouette', t('listing.dressSilhouetteRequired')],
        ['neckline', t('listing.necklineRequired')],
        ['sleeves', t('listing.sleevesRequired')],
        ['back_design', t('listing.backDesignRequired')],
        ['train', t('listing.trainRequired')],
        ['vibe', t('listing.vibeRequired')],
        ['main_fabric', t('listing.mainFabricRequired')],
        ['fabric_color', t('listing.dressColorRequired')],
        ['bust_cm', t('listing.bustRequired')],
        ['waist_cm', t('listing.waistRequired')],
        ['hips_cm', t('listing.hipsRequired')],
        ['worn_height_cm', t('listing.wornHeightRequired')],
        ['weight_range_kg', t('listing.weightRangeRequired')],
        ['size_label', t('listing.sizeLabelRequired')],
        ['adjustability', t('listing.adjustableFeatureRequired')],
        ['weight_class', t('listing.dressWeightRequired')],
        ['mobility', t('listing.mobilityRequired')],
        ['danceable', t('listing.danceableRequired')],
        ['sale_price', t('listing.salePriceRequired')],
      ];
      requiredDressFields.forEach(([field, message]) => {
        if (!String(weddingDress[field] || '').trim()) {
          newErrors[`dress_${field}`] = message;
        }
      });
      if (!formData.is_hijabi) {
        newErrors.is_hijabi = t('listing.hijabiRequired');
      }
      if (!weddingDress.embellishments.length) {
        newErrors.dress_embellishments = t('listing.embellishmentsRequired');
      }
      if (!weddingDress.extras.length) {
        newErrors.dress_extras = t('listing.extrasRequired');
      }
      const dressPrice = parseFloat(weddingDress.sale_price);
      if (weddingDress.sale_price.trim() && (Number.isNaN(dressPrice) || dressPrice <= 0)) {
        newErrors.dress_sale_price = t('listing.priceInvalid');
      }
    }
    if (isGroomStructuredListing) {
      const required = [
        ['suit_type', t('listing.suitTypeRequired')],
        ['fit', t('listing.fitRequired')],
        ['lapel', t('listing.lapelRequired')],
        ['closure', t('listing.closureRequired')],
        ['pant_style', t('listing.pantStyleRequired')],
        ['vibe', t('listing.vibeRequired')],
        ['main_fabric', t('listing.mainFabricRequired')],
        ['pattern', t('listing.patternRequired')],
        ['color', t('listing.colorRequired')],
        ['chest_cm', t('listing.chestRequired')],
        ['waist_cm', t('listing.waistRequired')],
        ['shoulder_cm', t('listing.shoulderRequired')],
        ['sleeve_cm', t('listing.sleeveRequired')],
        ['trouser_waist_in', t('listing.trouserWaistRequired')],
        ['trouser_length_in', t('listing.trouserLengthRequired')],
        ['height_cm', t('listing.heightRequired')],
        ['weight_range_kg', t('listing.weightRangeRequired')],
        ['size_label', t('listing.sizeLabelRequired')],
        ['adjustability', t('listing.adjustabilityRequired')],
        ['weight_class', t('listing.outfitWeightRequired')],
        ['mobility', t('listing.mobilityRequired')],
        ['danceable', t('listing.danceableRequired')],
        ['sale_price', t('listing.salePriceRequired')],
      ];
      required.forEach(([field, msg]) => {
        if (!String(groomSuit[field] || '').trim()) newErrors[`groom_${field}`] = msg;
      });
      if (!groomSuit.included_items.length) {
        newErrors.groom_included_items = t('listing.includedItemsRequired');
      }
      const groomPrice = parseFloat(groomSuit.sale_price);
      if (groomSuit.sale_price.trim() && (Number.isNaN(groomPrice) || groomPrice <= 0)) {
        newErrors.groom_sale_price = t('listing.priceInvalid');
      }
    }


    setErrors(newErrors);
    return newErrors;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const toggleSize = (sizeValue) => {
    setSelectedSizes((prev) =>
      prev.includes(sizeValue) ? prev.filter((s) => s !== sizeValue) : [...prev, sizeValue],
    );
    if (errors.sizes) {
      setErrors((prev) => ({ ...prev, sizes: '' }));
    }
  };

  const handleSuggestionSelect = (itemName) => {
    setFormData(prev => ({ ...prev, title: itemName }));
    // Scroll to title input
    const titleInput = document.getElementById('title');
    if (titleInput) {
      titleInput.focus();
      titleInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    toast.success(t('listing.itemAddedToTitle', { name: itemName }));
  };

  const handleImageChange = (e) => {
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      toast.error(t('listing.imageFilesOnly'));
      return;
    }

    const validFiles = [];
    for (const file of imageFiles) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(t('listing.unsupportedFormat', { name: file.name }));
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(t('listing.imageTooLarge', { name: file.name }));
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    const startIndex = selectedImages.length;
    const newImages = validFiles.map(file => ({
      id: `${Date.now()}_${++imageIdRef.current}`,
      file,
      preview: URL.createObjectURL(file),
      upload: { status: 'pending', progress: 0 },
    }));

    setSelectedImages((prev) => [...prev, ...newImages]);

    const queue = newImages.map((_, i) => startIndex + i);
    const [first, ...rest] = queue;
    setCropQueue(rest);
    setCropModal({
      index: first,
      src: newImages[0].preview,
      fileName: newImages[0].file?.name || 'photo.jpg',
    });
  };

  const openCropForIndex = (index) => {
    const image = selectedImages[index];
    if (!image?.preview) return;
    setCropQueue([]);
    setCropModal({
      index,
      src: image.preview,
      fileName: image.file?.name || `photo_${index + 1}.jpg`,
    });
  };

  const advanceCropQueue = (images, queue) => {
    if (!queue.length) {
      setCropModal(null);
      setCropQueue([]);
      return;
    }
    const [index, ...rest] = queue;
    const image = images[index];
    if (!image?.preview) {
      advanceCropQueue(images, rest);
      return;
    }
    setCropQueue(rest);
    setCropModal({
      index,
      src: image.preview,
      fileName: image.file?.name || `photo_${index + 1}.jpg`,
    });
  };

  const updateImageUpload = (id, patch) => {
    setSelectedImages((prev) =>
      prev.map((img) => (img.id === id ? { ...img, upload: { ...img.upload, ...patch } } : img)),
    );
  };

  // Starts (or restarts) the pick-time upload for one image. Each photo uploads
  // to R2 in the background while the user keeps picking/editing; at submit time
  // only the returned keys need to be attached to the draft.
  const startImageUpload = (image, { fresh = false } = {}) => {
    if (!image?.file || !image?.id) return Promise.resolve({ skipped: true });
    uploadControllersRef.current.get(image.id)?.abort();
    const begin = async () => {
      if (fresh) {
        // The file changed (re-crop): discard the previous multipart session so
        // the new file never resumes the old one.
        await cancelPendingUpload(image.id);
      }
      const controller = new AbortController();
      uploadControllersRef.current.set(image.id, controller);
      updateImageUpload(image.id, { status: 'uploading', progress: 0, key: undefined, error: undefined });
      const promise = uploadPendingImage({
        file: image.file,
        imageId: image.id,
        signal: controller.signal,
        onProgress: (label, meta) => {
          if (typeof meta?.percent === 'number') {
            updateImageUpload(image.id, { status: 'uploading', progress: meta.percent });
          }
        },
      })
        .then((result) => {
          uploadControllersRef.current.delete(image.id);
          if (result?.deferred) {
            // Server has no direct-upload storage: upload at submit time instead.
            updateImageUpload(image.id, { status: 'deferred', progress: 100 });
            return { deferred: true };
          }
          updateImageUpload(image.id, { status: 'done', progress: 100, key: result.key });
          return { key: result.key };
        })
        .catch((err) => {
          uploadControllersRef.current.delete(image.id);
          if (isAbortError(err)) return { aborted: true };
          const msg = err?.response?.data?.error || err?.message || 'upload failed';
          updateImageUpload(image.id, { status: 'error', error: String(msg) });
          return { error: msg };
        });
      return promise;
    };
    const run = begin();
    uploadPromisesRef.current.set(image.id, run);
    return run;
  };

  const retryImageUpload = (index) => {
    const image = selectedImages[index];
    if (image) {
      void startImageUpload(image);
    }
  };

  const applyCropResult = ({ file, preview }) => {
    if (!cropModal) return;
    const { index } = cropModal;
    let nextImages = selectedImages;
    let croppedEntry = null;
    setSelectedImages((prev) => {
      const next = [...prev];
      const old = next[index];
      if (old?.preview) URL.revokeObjectURL(old.preview);
      croppedEntry = {
        id: old?.id || `${Date.now()}_${++imageIdRef.current}`,
        file,
        preview,
        upload: { status: 'pending', progress: 0 },
      };
      next[index] = croppedEntry;
      nextImages = next;
      return next;
    });
    if (croppedEntry) {
      void startImageUpload(croppedEntry, { fresh: true });
    }
    advanceCropQueue(nextImages.map((img, i) => (i === index ? croppedEntry || img : img)), cropQueue);
  };

  const skipCrop = () => {
    if (cropModal) {
      const image = selectedImages[cropModal.index];
      if (image && (!image.upload || image.upload.status === 'pending')) {
        void startImageUpload(image);
      }
    }
    advanceCropQueue(selectedImages, cropQueue);
  };

  const removeImage = (index) => {
    const target = selectedImages[index];
    if (target?.id) {
      uploadControllersRef.current.get(target.id)?.abort();
      uploadControllersRef.current.delete(target.id);
      uploadPromisesRef.current.delete(target.id);
      void cancelPendingUpload(target.id);
    }
    setSelectedImages(prev => {
      const newImages = [...prev];
      if (newImages[index]?.preview) URL.revokeObjectURL(newImages[index].preview); // Clean up object URL
      newImages.splice(index, 1);
      return newImages;
    });
    setCropModal((cur) => {
      if (!cur) return null;
      if (cur.index === index) return null;
      if (cur.index > index) return { ...cur, index: cur.index - 1 };
      return cur;
    });
  };

  const persistListingLocation = async () => {
    setSavingAddress(true);
    try {
      const payload = {
        shipping_address: shippingAddress.trim(),
        latitude: addressCoords.latitude,
        longitude: addressCoords.longitude,
      };
      const response = await authAPI.updateProfile(payload);
      dispatch({ type: 'auth/updateUser', payload: response });
    } finally {
      setSavingAddress(false);
    }
  };

  const listingLocationChanged = () => {
    const currentAddress = shippingAddress.trim();
    const savedAddress = (user?.shipping_address || '').trim();
    return (
      currentAddress !== savedAddress
      || addressCoords.latitude !== (user?.latitude ?? null)
      || addressCoords.longitude !== (user?.longitude ?? null)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.error(t('listing.loginToCreate'));
      return;
    }

    const validationErrors = validateForm();
    const missingFieldMessages = Object.values(validationErrors).filter(Boolean);
    if (missingFieldMessages.length > 0) {
      toast.error(
        <div>
          <strong>{t('listing.missingFieldsToast')}</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
            {missingFieldMessages.slice(0, 6).map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
            {missingFieldMessages.length > 6 && (
              <li>{t('listing.moreFieldsHighlighted', { count: missingFieldMessages.length - 6 })}</li>
            )}
          </ul>
        </div>
      );
      setTimeout(() => {
        document.querySelector('.field-error-text')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }

    // Validate minimum 3 images required
    if (selectedImages.length < 3) {
      toast.error(t('listing.imagesRequiredCount', { count: selectedImages.length }));
      return;
    }

    // Show address confirmation step
    setShowAddressStep(true);
    return;
  };

  const handleConfirmAndSubmit = async () => {
    if (!shippingAddress.trim()) {
      toast.error(t('listing.locationRequiredBody'));
      return;
    }

    if (listingLocationChanged()) {
      try {
        await persistListingLocation();
      } catch (locationError) {
        const message = locationError?.response?.data?.error || t('listing.locationSaveFailed');
        toast.error(typeof message === 'string' ? message : t('listing.locationSaveFailed'));
        return;
      }
    }

    setShowAddressStep(false);
    setUploading(true);

    try {
      const itemData = {
        title: formData.title,
        description: formData.description,
        price: isStructuredWeddingFlow
          ? parseFloat(isWeddingDressListing ? (weddingDress.sale_price || '0') : (groomSuit.sale_price || '0'))
          : parseFloat(formData.price),
        category: formData.category,
        condition: formData.condition,
        in_stock: 1,
        weight_over_15kg: formData.weight_over_15kg || false,
        fragile: Boolean(formData.fragile),
        is_anonymous: formData.is_anonymous === 'true',
        phone_discoverable: isElectronicsListingCategory(formData.category),
        is_defected: formData.is_defected === 'true',
        is_luxury: resolveIsLuxuryListing(luxuryPieceMode, formData.price),
        ...(askReceiptOrGuarantee
          ? { has_receipt_or_guarantee: formData.has_receipt_or_guarantee === 'true' }
          : {}),
        ...(isWeddingDressListing && formData.is_hijabi !== ''
          ? { is_hijabi: formData.is_hijabi === 'true' }
          : {}),
        item_flaws: itemFlaws,
      };

      // Books taxonomy + filters
      if (isBooksListingCategory(formData.category)) {
        itemData.book_subcategory = joinBookSubcategoryPath([
          formData.book_section,
          formData.book_group,
          formData.book_topic,
        ]);
        itemData.book_type = formData.book_type;
        itemData.book_language = formData.book_language;
        itemData.book_audience = formData.book_audience;
      } else if (isWatchesListingCategory(formData.category)) {
        itemData.subcategory = joinWatchSubcategoryPath([
          formData.watch_department,
          formData.watch_subcategory,
        ]);
      } else if (isBagsListingCategory(formData.category)) {
        itemData.subcategory = joinBagSubcategoryPath([
          formData.bag_department,
          formData.bag_subcategory,
        ]);
      } else if (isFootwearListingCategory(formData.category)) {
        itemData.subcategory = joinFootwearSubcategoryPath([
          formData.footwear_department,
          formData.footwear_subcategory,
        ]);
      } else if (isAccessoriesListingCategory(formData.category)) {
        itemData.subcategory = joinAccessorySubcategoryPath([
          formData.accessory_department,
          formData.accessory_subcategory,
        ]);
      } else if (isClothingListingCategory(formData.category)) {
        itemData.subcategory = joinClothingSubcategoryPath([
          formData.clothing_audience,
          formData.clothing_type,
          formData.clothing_subtype,
          formData.clothing_style,
        ]);
      } else if (isPetSuppliesListingCategory(formData.category)) {
        itemData.subcategory = joinPetSubcategoryPath([
          formData.pet_section,
          formData.pet_group,
          formData.pet_product,
        ]);
        itemData.pet_type = formData.pet_type;
      } else if (
        isHomeKitchenListingCategory(formData.category) &&
        isKitchenDiningSubcategory(formData.subcategory)
      ) {
        itemData.subcategory = joinKitchenDiningSubcategoryPath([
          KITCHEN_DINING_ROOT,
          formData.kitchen_group,
          formData.kitchen_type,
        ]);
      } else if (formData.subcategory) {
        itemData.subcategory = formData.subcategory;
      }

      if (isWeddingDressListing) {
        itemData.color = weddingDress.fabric_color;
        itemData.size = weddingDress.size_label;
        itemData.available_sizes = weddingDress.size_label;
      } else if (isGroomStructuredListing) {
        itemData.color = groomSuit.color;
        itemData.size = groomSuit.size_label;
        itemData.available_sizes = groomSuit.size_label;
      } else if (listingAttrs) {
        Object.assign(itemData, buildSizeColorPayload(formData.color, selectedSizes));
      }

      if (isWeddingListing) {
        const department = getWeddingCategoryDepartment(formData.category);
        if (department) {
          itemData.department = department;
        } else {
          delete itemData.department;
        }
        if (formData.subcategory) {
          itemData.description = `Type: ${formData.subcategory}\n\n${itemData.description}`;
        }
      }

      if (isWeddingDressListing) {
        const normalized = buildWeddingDressJson({
          formData,
          dressData: weddingDress,
          imageNames: selectedImages.map((img) => img.file?.name || '').filter(Boolean),
          colorOptions: dressColorOptions,
        });
        itemData.description = `${itemData.description}\n\nStructured dress profile JSON:\n${JSON.stringify(normalized)}`;
        itemData.fragile = false;
        itemData.in_stock = 1;
      }
      if (isGroomStructuredListing) {
        const normalized = buildGroomSuitJson({
          formData,
          groomData: groomSuit,
          imageNames: selectedImages.map((img) => img.file?.name || '').filter(Boolean),
        });
        itemData.description = `${itemData.description}\n\nStructured groom suit profile JSON:\n${JSON.stringify(normalized)}`;
        itemData.fragile = false;
        itemData.in_stock = 1;
      }

      // Images are required, always use FormData
      if (selectedImages.length >= 3) {
        // Photos start uploading the moment they are picked. If any are still in
        // flight (or never started), wait for them before creating the draft.
        selectedImages
          .filter((img) => !img.upload || img.upload.status === 'pending' || img.upload.status === 'error')
          .forEach((img) => { void startImageUpload(img); });
        const inFlight = selectedImages
          .map((img) => uploadPromisesRef.current.get(img.id))
          .filter(Boolean);
        if (inFlight.length > 0) {
          setWaitingUploads(true);
          try {
            await Promise.all(inFlight);
          } finally {
            setWaitingUploads(false);
          }
        }

        const outcomes = await Promise.all(
          selectedImages.map((img) => uploadPromisesRef.current.get(img.id) || Promise.resolve(null)),
        );
        const failedPhotos = [];
        const orderedKeys = [];
        selectedImages.forEach((img, i) => {
          const result = outcomes[i];
          const key = result?.key || img.upload?.key;
          if (key) {
            orderedKeys.push(key);
          } else if (!(result?.deferred || img.upload?.status === 'deferred')) {
            failedPhotos.push(img);
          }
        });
        if (failedPhotos.length > 0) {
          const failReasons = failedPhotos
            .map((img) => img.upload?.error || 'no_key')
            .join('; ');
          trackListingCreateFailed({
            category: formData.category,
            error: `image_upload_failed: ${failReasons}`,
            status: null,
          });
          toast.error(t('listing.photosUploadFailedRetry', { count: failedPhotos.length }));
          return;
        }

        const formDataToSend = new FormData();
        
        // Add item data
        Object.keys(itemData).forEach(key => {
          const val = itemData[key];
          if (key === 'item_flaws' && Array.isArray(val)) {
            formDataToSend.append(key, JSON.stringify(val));
          } else if (typeof val === 'boolean') {
            formDataToSend.append(key, val ? 'true' : 'false');
          } else if (val !== undefined && val !== null && val !== '') {
            formDataToSend.append(key, String(val));
          }
        });
        
        // Image bytes are not sent with the draft: photos were uploaded at pick
        // time and are attached below via their R2 keys.
        appendBrandFields(formDataToSend, brandSelection);

        const listedAsLuxury = resolveIsLuxuryListing(luxuryPieceMode, formData.price);
        const listingType = isWeddingListing
          ? 'wedding_essentials'
          : listedAsLuxury
            ? 'luxury'
            : formData.is_defected === 'true'
              ? 'defected'
              : 'pre-owned';
        const analyticsBase = {
          category: formData.category,
          listing_type: listingType,
        };

        trackListingCreateStarted(analyticsBase);

        const DRAFT_KEY = 'flea.listing_upload_draft.v1';
        let draftId = null;
        try {
          const raw = sessionStorage.getItem(DRAFT_KEY);
          if (raw) {
            const prior = JSON.parse(raw);
            const names = selectedImages.map((img) => img.file?.name || '').join('|');
            if (prior?.draftId && prior?.photoKey === names) {
              draftId = prior.draftId;
            } else {
              sessionStorage.removeItem(DRAFT_KEY);
            }
          }
        } catch { /* ignore */ }

        if (draftId == null) {
          try {
            const draft = await itemsAPI.createItemDraft(formDataToSend);
            draftId = draft?.item_id;
            if (draftId == null) throw new Error(t('listing.draftCreateNoId'));
          } catch (createErr) {
            const errMsg =
              (typeof createErr === 'string' && createErr) ||
              createErr?.response?.data?.error ||
              createErr?.error ||
              createErr?.message ||
              'Failed to create draft';
            trackListingCreateFailed({
              ...analyticsBase,
              error: errMsg,
              status: createErr?.response?.status ?? createErr?.status ?? null,
              request_id: requestIdFromError(createErr),
            });
            throw createErr;
          }
        }

        const photoKey = selectedImages.map((img) => img.file?.name || '').join('|');

        // Attach photos uploaded at pick time (idempotent on the backend, so a
        // retried submit never duplicates images).
        if (orderedKeys.length > 0) {
          try {
            await itemsAPI.attachPendingImages(draftId, orderedKeys);
          } catch (attachErr) {
            const attachMsg =
              attachErr?.response?.data?.error || attachErr?.message || t('listing.photoUploadFailed');
            trackListingCreateFailed({
              ...analyticsBase,
              error: `image_attach_failed: ${attachMsg}`,
              status: attachErr?.response?.status ?? null,
              item_id: draftId,
              request_id: requestIdFromError(attachErr),
            });
            throw new Error(attachMsg);
          }
        }

        // Fallback for photos that could not use direct upload (server storage in
        // proxy mode): upload them now through the item images endpoint.
        const uploadedFlags = [];
        try {
          const raw = sessionStorage.getItem(DRAFT_KEY);
          if (raw) {
            const prior = JSON.parse(raw);
            if (prior?.draftId === draftId && Array.isArray(prior.uploaded)) {
              prior.uploaded.forEach((v, i) => { uploadedFlags[i] = !!v; });
            }
          }
        } catch { /* ignore */ }

        // Persist the draft id so a retry after a later failure reuses it instead
        // of creating a duplicate draft.
        try {
          sessionStorage.setItem(DRAFT_KEY, JSON.stringify({
            draftId,
            photoKey,
            uploaded: uploadedFlags,
          }));
        } catch { /* ignore */ }

        for (let i = 0; i < selectedImages.length; i++) {
          if (outcomes[i]?.key) continue; // already attached above
          if (uploadedFlags[i]) continue;
          const file = selectedImages[i].file;
          try {
            await itemsAPI.uploadItemImage(draftId, file);
            uploadedFlags[i] = true;
            sessionStorage.setItem(DRAFT_KEY, JSON.stringify({
              draftId,
              photoKey,
              uploaded: uploadedFlags,
            }));
          } catch (imgErr) {
            const imgMsg =
              imgErr?.response?.data?.error || imgErr?.message || t('listing.photoUploadFailed');
            trackListingCreateFailed({
              ...analyticsBase,
              error: `image_upload_failed: ${imgMsg}`,
              status: imgErr?.response?.status ?? null,
              item_id: draftId,
              request_id: requestIdFromError(imgErr),
            });
            throw new Error(
              t('listing.photoUploadResume', { index: i + 1, total: selectedImages.length, reason: imgMsg }),
            );
          }
        }

        let finalized;
        try {
          finalized = await itemsAPI.finalizeItemDraft(draftId);
        } catch (finErr) {
          const finMsg =
            finErr?.response?.data?.error || finErr?.message || 'Failed to publish listing';
          trackListingCreateFailed({
            ...analyticsBase,
            error: finMsg,
            status: finErr?.response?.status ?? null,
            item_id: draftId,
            request_id: requestIdFromError(finErr),
          });
          throw finErr;
        }

        const itemId = finalized?.item_id ?? draftId;
        sessionStorage.removeItem(DRAFT_KEY);

        try {
          await itemsAPI.getItemById(itemId);
        } catch {
          trackListingCreateFailed({
            ...analyticsBase,
            error: 'could_not_confirm_listing',
            status: 'confirm_failed',
            item_id: itemId,
          });
          toast.error(t('listing.couldntConfirmToast'));
          setUploading(false);
          return;
        }

        trackListingCreateSucceeded({
          ...analyticsBase,
          item_id: itemId,
        });

        const pendingAdminReview = finalized?.approval_status === 'PENDING';

        if (pendingAdminReview) {
          toast.success(t('listing.pendingAdminApproval'), { duration: 9000 });
          navigate('/my-catalogue');
        } else {
          toast.success(t('listing.itemCreated'));
          navigate(
            isWeddingListing
              ? exitPath
              : hobbiesEntryMode
                ? '/hobbies-finds'
                : listedAsLuxury
                  ? '/luxury'
                  : `/items/${itemId}`,
          );
        }
      } else {
        // This should not happen due to validation above, but keep as fallback
        toast.error(t('listing.imagesRequiredBody'));
        setUploading(false);
        return;
      }
    } catch (error) {
      console.error('Error creating item:', error);
      const errorMessage =
        (typeof error === 'string' && error) ||
        error?.error ||
        error?.response?.data?.error ||
        error?.message ||
        t('listing.createItemFailed');

      // Parse backend validation errors and display them inline
      const backendErrors = {};
      if (errorMessage.includes('Title')) {
        if (errorMessage.includes('at least 3 characters')) {
          backendErrors.title = t('listing.titleTooShort');
        } else if (errorMessage.includes('less than 200 characters')) {
          backendErrors.title = t('listing.titleTooLong');
        } else if (errorMessage.includes('suspicious content')) {
          backendErrors.title = t('listing.titleSuspicious');
        }
      }
      if (errorMessage.includes('Description')) {
        if (errorMessage.includes('at least')) {
          backendErrors.description = t('listing.descriptionTooShort');
        } else if (errorMessage.includes('less than 5000 characters')) {
          backendErrors.description = t('listing.descriptionTooLong');
        } else if (errorMessage.includes('suspicious content')) {
          backendErrors.description = t('listing.descriptionSuspicious');
        }
      }
      
      if (Object.keys(backendErrors).length > 0) {
        setErrors(prev => ({ ...prev, ...backendErrors }));
        // Scroll to first error field
        const firstErrorField = Object.keys(backendErrors)[0];
        const errorInput = document.getElementById(firstErrorField);
        if (errorInput) {
          errorInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorInput.focus();
        }
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setUploading(false);
    }
  };

  if (!user) {
    return (
      <PageWrapper>
        <PageInner>
          <PageHeader>
            <PageTitle>{t('listing.createNewItem')}</PageTitle>
            <PageSubtitle>{t('listing.loginToCreate')}</PageSubtitle>
          </PageHeader>
        </PageInner>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <PageInner>
        <PageHeader>
          <PageTitle $elegant={!luxuryPieceMode}>
            {luxuryPieceMode
              ? t('listing.offerLuxuryTitle')
              : showWeddingContext
                ? t('listing.listWeddingTitle')
                : hobbiesEntryMode
                  ? t('listing.listHobbiesTitle')
                  : defaultDefected
                    ? t('listing.defectedTitle')
                    : t('listing.defaultTitle')}
          </PageTitle>
          {hobbiesEntryMode && !defaultDefected ? (
            <PageSubtitle>
              {t('listing.hobbiesSubtitle')}
            </PageSubtitle>
          ) : null}
          {defaultDefected && (
            <PageSubtitle>
              {t('listing.defectedSubtitle')}
            </PageSubtitle>
          )}
        </PageHeader>

        {showWeddingContext ? (
          <WeddingEssentialsPolicy />
        ) : !luxuryPieceMode ? (
          <ItemSuggestions onItemSelect={handleSuggestionSelect} />
        ) : null}

        <Form onSubmit={handleSubmit}>
          {!defaultDefected && (
          <FormGroup>
            <ListingTypeSelector
              value={currentListingType}
              onChange={handleListingTypeChange}
            />
          </FormGroup>
          )}

          {!isWeddingListing && looksLikeWeddingOccasionClothing(formData) && (
            <ListingHint role="status">
              {t('listing.weddingHintPrefix')}{' '}
              <strong>{t('listing.bridalSoireeGroom')}</strong> {t('listing.weddingHintSuffix')}
              <div>
                <ListingHintButton
                  type="button"
                  onClick={() => switchToWeddingSection(SOIREE_CLOTHING_SECTION)}
                >
                  {t('listing.useSoireeCategory')}
                </ListingHintButton>
              </div>
            </ListingHint>
          )}

          <FormGroup>
            <Label>{t('listing.postingPrivacy')} *</Label>
            <RadioGroup>
              <RadioOption $active={formData.is_anonymous === 'false'}>
                <input
                  type="radio"
                  name="is_anonymous"
                  value="false"
                  checked={formData.is_anonymous === 'false'}
                  onChange={(e) => handleInputChange('is_anonymous', e.target.value)}
                />
                👤 {t('listing.showMyUsername')}
              </RadioOption>
              <RadioOption $active={formData.is_anonymous === 'true'}>
                <input
                  type="radio"
                  name="is_anonymous"
                  value="true"
                  checked={formData.is_anonymous === 'true'}
                  onChange={(e) => handleInputChange('is_anonymous', e.target.value)}
                />
                🙈 {t('listing.postAnonymously')}
              </RadioOption>
            </RadioGroup>
            {errors.is_anonymous && <ErrorText>{errors.is_anonymous}</ErrorText>}
          </FormGroup>

          {askReceiptOrGuarantee && (
          <FormGroup>
            <Label>{t('listing.receiptQuestion')} *</Label>
            <RadioGroup>
              <RadioOption $active={formData.has_receipt_or_guarantee === 'true'}>
                <input
                  type="radio"
                  name="has_receipt_or_guarantee"
                  value="true"
                  checked={formData.has_receipt_or_guarantee === 'true'}
                  onChange={(e) => handleInputChange('has_receipt_or_guarantee', e.target.value)}
                />
                {t('listing.yes')}
              </RadioOption>
              <RadioOption $active={formData.has_receipt_or_guarantee === 'false'}>
                <input
                  type="radio"
                  name="has_receipt_or_guarantee"
                  value="false"
                  checked={formData.has_receipt_or_guarantee === 'false'}
                  onChange={(e) => handleInputChange('has_receipt_or_guarantee', e.target.value)}
                />
                {t('listing.no')}
              </RadioOption>
            </RadioGroup>
            {errors.has_receipt_or_guarantee && <ErrorText>{errors.has_receipt_or_guarantee}</ErrorText>}
          </FormGroup>
          )}

          <ListingPhotosGuidance photoCount={selectedImages.length} />

          <FormGroup>
            <Label>{t('listing.photos')} *</Label>
            {selectedImages.length > 0 && selectedImages.length < 3 && (
              <p style={{ fontSize: '0.85rem', color: '#dc3545', marginTop: '0.25rem', fontWeight: 500 }}>
                {t('listing.addMorePhotos', { count: 3 - selectedImages.length })}
              </p>
            )}
            <Input
              ref={photoInputRef}
              id="images"
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageChange}
              style={{ display: 'none' }}
            />
            <PhotoSlotGrid
              items={selectedImages}
              maxSlots={12}
              onPickSlot={() => photoInputRef.current?.click()}
              onRemove={removeImage}
              onEdit={openCropForIndex}
              onRetry={retryImageUpload}
            />
            {selectedImages.length > 1 ? (
              <ImageOrderHint>
                {t('listing.photoOrderHintEdit')}
              </ImageOrderHint>
            ) : (
              <ImageOrderHint>
                {t('listing.photoPickHint')}
              </ImageOrderHint>
            )}
          </FormGroup>

          {cropModal ? (
            <ImageCropModal
              imageSrc={cropModal.src}
              fileName={cropModal.fileName}
              remainingAfter={cropQueue.length}
              onCancel={skipCrop}
              onComplete={applyCropResult}
            />
          ) : null}

          {!isStructuredWeddingFlow && (
            <>
              <FormGroup>
                <Label htmlFor="title">{t('listing.itemTitle')} *</Label>
                <Input
                  id="title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder={t('listing.itemTitlePlaceholder')}
                  maxLength={200}
                  error={errors.title}
                />
                {errors.title && <ErrorText>{errors.title}</ErrorText>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="description">{t('listing.description')} *</Label>
                <TextArea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder={t('listing.descriptionPlaceholder')}
                  rows={4}
                  error={errors.description}
                />
                {errors.description && <ErrorText>{errors.description}</ErrorText>}
              </FormGroup>
            </>
          )}

          <FormGroup>
              <Label htmlFor="price">{t('listing.price')} *</Label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <Input
                  id="price"
                  type="text"
                  inputMode="decimal"
                  dir="ltr"
                  value={
                    isStructuredWeddingFlow
                      ? (isWeddingDressListing ? weddingDress.sale_price : groomSuit.sale_price)
                      : formData.price
                  }
                  onChange={(e) => {
                    const next = sanitizeDecimalInput(e.target.value);
                    if (isStructuredWeddingFlow) {
                      if (isWeddingDressListing) {
                        updateWeddingDress('sale_price', next);
                      } else {
                        updateGroomSuit('sale_price', next);
                      }
                      return;
                    }
                    handleInputChange('price', next);
                  }}
                  placeholder="0.00"
                  error={
                    isStructuredWeddingFlow
                      ? (isWeddingDressListing ? errors.dress_sale_price : errors.groom_sale_price)
                      : errors.price
                  }
                  style={{ maxWidth: '180px' }}
                />
              </div>
              {isStructuredWeddingFlow
                ? (isWeddingDressListing
                  ? errors.dress_sale_price && <ErrorText>{errors.dress_sale_price}</ErrorText>
                  : errors.groom_sale_price && <ErrorText>{errors.groom_sale_price}</ErrorText>)
                : errors.price && <ErrorText>{errors.price}</ErrorText>}
            </FormGroup>

          {isElectronicsListingCategory(formData.category) && (
            <FormGroup>
              <Label>{t('listing.contactOnly')}</Label>
              <p style={{ fontSize: '0.9rem', color: '#475569', lineHeight: 1.5, margin: '0 0 0.75rem' }}>
                {t('listing.contactOnlyBody')}
              </p>
            </FormGroup>
          )}

          <FormGroup>
            <Label htmlFor="category">{t('listing.category')} *</Label>
            <Select
              id="category"
              value={isWeddingListing ? 'Clothing' : formData.category}
              onChange={(e) => {
                const category = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  ...getTaxonomyResetPatch(),
                  category,
                  book_subcategory: '',
                  pet_type: '',
                  pet_section: '',
                  pet_group: '',
                  pet_product: '',
                  book_section: '',
                  book_group: '',
                  book_topic: '',
                  book_type: '',
                  book_language: '',
                  book_audience: '',
                  phone_discoverable: '',
                }));
                setClothingListingSection('');
                setPhoneDiscoverConfirm(false);
                clearWeddingAttributeSelections();
                setErrors((prev) => ({
                  ...prev,
                  category: '',
                  clothing_listing_section: '',
                  wedding_category: '',
                  subcategory: '',
                }));
              }}
              error={errors.category}
            >
              <option value="">{t('listing.selectCategory')}</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </Select>
            {errors.category && <ErrorText>{errors.category}</ErrorText>}
          </FormGroup>

          {(formData.category === 'Clothing' || isWeddingListing) && (
            <FormGroup>
              <Label htmlFor="clothing_listing_section">{t('listing.clothingSection')} *</Label>
              <Select
                id="clothing_listing_section"
                value={clothingListingSection}
                onChange={(e) => {
                  const section = e.target.value;
                  const occasionCategory = apiCategoryForClothingSection(section);
                  setClothingListingSection(section);
                  setFormData((prev) => ({
                    ...prev,
                    ...getTaxonomyResetPatch(),
                    category: occasionCategory || 'Clothing',
                    subcategory: '',
                    condition: occasionCategory
                      ? (prev.condition || 'Used')
                      : prev.condition,
                    is_defected: occasionCategory ? 'false' : prev.is_defected,
                  }));
                  clearWeddingAttributeSelections();
                  setErrors((prev) => ({
                    ...prev,
                    clothing_listing_section: '',
                    wedding_category: '',
                    subcategory: '',
                  }));
                }}
                error={errors.clothing_listing_section}
              >
                <option value="">{t('listing.selectClothingSection')}</option>
                {!weddingEntryMode && (
                  <option value={EVERYDAY_CLOTHING_SECTION}>{EVERYDAY_CLOTHING_SECTION}</option>
                )}
                {CLOTHING_OCCASION_SECTION_NAMES.map((section) => (
                  <option key={section} value={section}>{section}</option>
                ))}
              </Select>
              {errors.clothing_listing_section && (
                <ErrorText>{errors.clothing_listing_section}</ErrorText>
              )}
            </FormGroup>
          )}

          {isOccasionClothingSection(clothingListingSection)
            && isWeddingListing
            && getSubcategories(formData.category).length > 0 && (
            <FormGroup>
              <Label htmlFor="wedding_subcategory">{t('listing.itemType')} *</Label>
              <Select
                id="wedding_subcategory"
                value={formData.subcategory}
                onChange={(e) => {
                  handleInputChange('subcategory', e.target.value);
                  clearWeddingAttributeSelections();
                }}
                error={errors.subcategory}
              >
                <option value="">{t('listing.selectItemType')}</option>
                {getSubcategories(formData.category).map((subcategory) => (
                  <option key={subcategory} value={subcategory}>{subcategory}</option>
                ))}
              </Select>
              {errors.subcategory && <ErrorText>{errors.subcategory}</ErrorText>}
            </FormGroup>
          )}

          {formData.category === 'Computers & Electronics' && (
            <FormGroup>
              <Label>{t('listing.importantNotice')}</Label>
              <p style={{ fontSize: '0.9rem', color: '#1e40af', lineHeight: 1.5, margin: '0 0 0.75rem', padding: '0.75rem 0.9rem', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 12 }}>
                {t('listing.electronicsNotice')}
              </p>
            </FormGroup>
          )}

          {!isWeddingListing
            && formData.category
            && getSubcategories(formData.category).length > 0 && (
            <FormGroup>
              <Label htmlFor="subcategory">{t('listing.subcategory')} *</Label>
              <Select
                id="subcategory"
                value={
                  isKitchenDiningSubcategory(formData.subcategory)
                    ? KITCHEN_DINING_ROOT
                    : formData.subcategory
                }
                onChange={(e) => {
                  handleInputChange('subcategory', e.target.value);
                  handleInputChange('kitchen_group', '');
                  handleInputChange('kitchen_type', '');
                }}
                error={errors.subcategory}
              >
                <option value="">{t('listing.selectSubcategory')}</option>
                {getSubcategories(formData.category).map((sub) => (
                  <option key={sub} value={sub}>
                    {sub}
                  </option>
                ))}
              </Select>
              {errors.subcategory && <ErrorText>{errors.subcategory}</ErrorText>}
            </FormGroup>
          )}

          {isHomeKitchenListingCategory(formData.category) &&
            isKitchenDiningSubcategory(formData.subcategory) && (
            <>
              <FormGroup>
                <Label htmlFor="kitchen_group">{t('listing.kitchenCategory')} *</Label>
                <Select
                  id="kitchen_group"
                  value={formData.kitchen_group}
                  onChange={(e) => {
                    handleInputChange('kitchen_group', e.target.value);
                    handleInputChange('kitchen_type', '');
                  }}
                  error={errors.kitchen_group}
                >
                  <option value="">{t('listing.selectKitchenCategory')}</option>
                  {KITCHEN_DINING_GROUPS.map((group) => (
                    <option key={group} value={group}>{group}</option>
                  ))}
                </Select>
                {errors.kitchen_group && <ErrorText>{errors.kitchen_group}</ErrorText>}
              </FormGroup>

              {formData.kitchen_group && (
                <FormGroup>
                  <Label htmlFor="kitchen_type">{t('listing.kitchenType')} *</Label>
                  <Select
                    id="kitchen_type"
                    value={formData.kitchen_type}
                    onChange={(e) => handleInputChange('kitchen_type', e.target.value)}
                    error={errors.kitchen_type}
                  >
                    <option value="">{t('listing.selectKitchenType')}</option>
                    {getKitchenDiningTypes(formData.kitchen_group).map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </Select>
                  {errors.kitchen_type && <ErrorText>{errors.kitchen_type}</ErrorText>}
                </FormGroup>
              )}
            </>
          )}

          {isWatchesListingCategory(formData.category) && (
            <>
              <FormGroup>
                <Label htmlFor="watch_department">{t('listing.department')} *</Label>
                <Select
                  id="watch_department"
                  value={formData.watch_department}
                  onChange={(e) => {
                    handleInputChange('watch_department', e.target.value);
                    handleInputChange('watch_subcategory', '');
                  }}
                  error={errors.watch_department}
                >
                  <option value="">{t('listing.selectDepartment')}</option>
                  {WATCH_DEPARTMENTS.map((dept) => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </Select>
                {errors.watch_department && <ErrorText>{errors.watch_department}</ErrorText>}
              </FormGroup>

              {formData.watch_department && (
                <FormGroup>
                  <Label htmlFor="watch_subcategory">{t('listing.subcategory')} *</Label>
                  <Select
                    id="watch_subcategory"
                    value={formData.watch_subcategory}
                    onChange={(e) => handleInputChange('watch_subcategory', e.target.value)}
                    error={errors.watch_subcategory}
                  >
                    <option value="">{t('listing.selectSubcategoryShort')}</option>
                    {getWatchSubcategories(formData.watch_department).map((sub) => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </Select>
                  {errors.watch_subcategory && <ErrorText>{errors.watch_subcategory}</ErrorText>}
                </FormGroup>
              )}
            </>
          )}

          {isBagsListingCategory(formData.category) && (
            <>
              <FormGroup>
                <Label htmlFor="bag_department">{t('listing.department')} *</Label>
                <Select
                  id="bag_department"
                  value={formData.bag_department}
                  onChange={(e) => {
                    handleInputChange('bag_department', e.target.value);
                    handleInputChange('bag_subcategory', '');
                  }}
                  error={errors.bag_department}
                >
                  <option value="">{t('listing.selectDepartment')}</option>
                  {BAG_DEPARTMENTS.map((dept) => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </Select>
                {errors.bag_department && <ErrorText>{errors.bag_department}</ErrorText>}
              </FormGroup>

              {formData.bag_department && (
                <FormGroup>
                  <Label htmlFor="bag_subcategory">{t('listing.subcategory')} *</Label>
                  <Select
                    id="bag_subcategory"
                    value={formData.bag_subcategory}
                    onChange={(e) => handleInputChange('bag_subcategory', e.target.value)}
                    error={errors.bag_subcategory}
                  >
                    <option value="">{t('listing.selectSubcategoryShort')}</option>
                    {getBagSubcategories(formData.bag_department).map((sub) => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </Select>
                  {errors.bag_subcategory && <ErrorText>{errors.bag_subcategory}</ErrorText>}
                </FormGroup>
              )}
            </>
          )}

          {isFootwearListingCategory(formData.category) && (
            <>
              <FormGroup>
                <Label htmlFor="footwear_department">{t('listing.department')} *</Label>
                <Select
                  id="footwear_department"
                  value={formData.footwear_department}
                  onChange={(e) => {
                    handleInputChange('footwear_department', e.target.value);
                    handleInputChange('footwear_subcategory', '');
                  }}
                  error={errors.footwear_department}
                >
                  <option value="">{t('listing.selectDepartment')}</option>
                  {FOOTWEAR_DEPARTMENTS.map((dept) => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </Select>
                {errors.footwear_department && <ErrorText>{errors.footwear_department}</ErrorText>}
              </FormGroup>

              {formData.footwear_department && (
                <FormGroup>
                  <Label htmlFor="footwear_subcategory">{t('listing.subcategory')} *</Label>
                  <Select
                    id="footwear_subcategory"
                    value={formData.footwear_subcategory}
                    onChange={(e) => handleInputChange('footwear_subcategory', e.target.value)}
                    error={errors.footwear_subcategory}
                  >
                    <option value="">{t('listing.selectSubcategoryShort')}</option>
                    {getFootwearSubcategories(formData.footwear_department).map((sub) => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </Select>
                  {errors.footwear_subcategory && <ErrorText>{errors.footwear_subcategory}</ErrorText>}
                </FormGroup>
              )}
            </>
          )}

          {isAccessoriesListingCategory(formData.category) && (
            <>
              <FormGroup>
                <Label htmlFor="accessory_department">{t('listing.department')} *</Label>
                <Select
                  id="accessory_department"
                  value={formData.accessory_department}
                  onChange={(e) => {
                    handleInputChange('accessory_department', e.target.value);
                    handleInputChange('accessory_subcategory', '');
                  }}
                  error={errors.accessory_department}
                >
                  <option value="">{t('listing.selectDepartment')}</option>
                  {ACCESSORY_DEPARTMENTS.map((dept) => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </Select>
                {errors.accessory_department && <ErrorText>{errors.accessory_department}</ErrorText>}
              </FormGroup>

              {formData.accessory_department && (
                <FormGroup>
                  <Label htmlFor="accessory_subcategory">{t('listing.subcategory')} *</Label>
                  <Select
                    id="accessory_subcategory"
                    value={formData.accessory_subcategory}
                    onChange={(e) => handleInputChange('accessory_subcategory', e.target.value)}
                    error={errors.accessory_subcategory}
                  >
                    <option value="">{t('listing.selectSubcategoryShort')}</option>
                    {getAccessorySubcategories(formData.accessory_department).map((sub) => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </Select>
                  {errors.accessory_subcategory && <ErrorText>{errors.accessory_subcategory}</ErrorText>}
                </FormGroup>
              )}
            </>
          )}

          {formData.category === 'Clothing'
            && clothingListingSection === EVERYDAY_CLOTHING_SECTION && (
            <>
              <FormGroup>
                <Label htmlFor="clothing_audience">{t('listing.department')} *</Label>
                <Select
                  id="clothing_audience"
                  value={formData.clothing_audience}
                  onChange={(e) => {
                    handleInputChange('clothing_audience', e.target.value);
                    handleInputChange('clothing_type', '');
                    handleInputChange('clothing_subtype', '');
                    handleInputChange('clothing_style', '');
                  }}
                  error={errors.clothing_audience}
                >
                  <option value="">{t('listing.selectDepartment')}</option>
                  {CLOTHING_AUDIENCES.map((audience) => (
                    <option key={audience} value={audience}>{audience}</option>
                  ))}
                </Select>
                {errors.clothing_audience && <ErrorText>{errors.clothing_audience}</ErrorText>}
              </FormGroup>

              {formData.clothing_audience && (
                <FormGroup>
                  <Label htmlFor="clothing_type">{t('listing.clothingType')} *</Label>
                  <Select
                    id="clothing_type"
                    value={formData.clothing_type}
                    onChange={(e) => {
                      handleInputChange('clothing_type', e.target.value);
                      handleInputChange('clothing_subtype', '');
                      handleInputChange('clothing_style', '');
                    }}
                    error={errors.clothing_type}
                  >
                    <option value="">{t('listing.selectClothingType')}</option>
                    {getClothingTypes(formData.clothing_audience).map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </Select>
                  {errors.clothing_type && <ErrorText>{errors.clothing_type}</ErrorText>}
                </FormGroup>
              )}

              {formData.clothing_type && (
                <FormGroup>
                  <Label htmlFor="clothing_subtype">{t('listing.subtype')} *</Label>
                  <Select
                    id="clothing_subtype"
                    value={formData.clothing_subtype}
                    onChange={(e) => {
                      handleInputChange('clothing_subtype', e.target.value);
                      handleInputChange('clothing_style', '');
                    }}
                    error={errors.clothing_subtype}
                  >
                    <option value="">{t('listing.selectSubtype')}</option>
                    {getClothingSubtypes(formData.clothing_audience, formData.clothing_type).map((subtype) => (
                      <option key={subtype} value={subtype}>{subtype}</option>
                    ))}
                  </Select>
                  {errors.clothing_subtype && <ErrorText>{errors.clothing_subtype}</ErrorText>}
                </FormGroup>
              )}

              {formData.clothing_subtype && (
                <FormGroup>
                  <Label htmlFor="clothing_style">{t('listing.style')} *</Label>
                  <Select
                    id="clothing_style"
                    value={formData.clothing_style}
                    onChange={(e) => handleInputChange('clothing_style', e.target.value)}
                    error={errors.clothing_style}
                  >
                    <option value="">{t('listing.selectStyle')}</option>
                    {getClothingStyles(
                      formData.clothing_audience,
                      formData.clothing_type,
                      formData.clothing_subtype,
                    ).map((style) => (
                      <option key={style} value={style}>{style}</option>
                    ))}
                  </Select>
                  {errors.clothing_style && <ErrorText>{errors.clothing_style}</ErrorText>}
                </FormGroup>
              )}
            </>
          )}

          {isPetSuppliesListingCategory(formData.category) && (
            <>
              <FormGroup>
                <Label htmlFor="pet_type">{t('listing.petType')} *</Label>
                <Select
                  id="pet_type"
                  value={formData.pet_type}
                  onChange={(e) => handleInputChange('pet_type', e.target.value)}
                  error={errors.pet_type}
                >
                  <option value="">{t('listing.selectPetType')}</option>
                  {PET_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Select>
                {errors.pet_type && <ErrorText>{errors.pet_type}</ErrorText>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="pet_section">{t('listing.category')} *</Label>
                <Select
                  id="pet_section"
                  value={formData.pet_section}
                  onChange={(e) => {
                    handleInputChange('pet_section', e.target.value);
                    handleInputChange('pet_group', '');
                    handleInputChange('pet_product', '');
                  }}
                  error={errors.pet_section}
                >
                  <option value="">{t('listing.selectCategoryShort')}</option>
                  {PET_SUPPLIES_SECTIONS.map((section) => (
                    <option key={section} value={section}>{section}</option>
                  ))}
                </Select>
                {errors.pet_section && <ErrorText>{errors.pet_section}</ErrorText>}
              </FormGroup>

              {formData.pet_section && (
                <FormGroup>
                  <Label htmlFor="pet_group">{t('listing.subcategory')} *</Label>
                  <Select
                    id="pet_group"
                    value={formData.pet_group}
                    onChange={(e) => {
                      handleInputChange('pet_group', e.target.value);
                      handleInputChange('pet_product', '');
                    }}
                    error={errors.pet_group}
                  >
                    <option value="">{t('listing.selectSubcategoryShort')}</option>
                    {getPetGroups(formData.pet_section).map((group) => (
                      <option key={group} value={group}>{group}</option>
                    ))}
                  </Select>
                  {errors.pet_group && <ErrorText>{errors.pet_group}</ErrorText>}
                </FormGroup>
              )}

              {formData.pet_group && (
                <FormGroup>
                  <Label htmlFor="pet_product">{t('listing.productType')} *</Label>
                  <Select
                    id="pet_product"
                    value={formData.pet_product}
                    onChange={(e) => handleInputChange('pet_product', e.target.value)}
                    error={errors.pet_product}
                  >
                    <option value="">{t('listing.selectProductType')}</option>
                    {getPetProducts(formData.pet_section, formData.pet_group).map((product) => (
                      <option key={product} value={product}>{product}</option>
                    ))}
                  </Select>
                  {errors.pet_product && <ErrorText>{errors.pet_product}</ErrorText>}
                </FormGroup>
              )}
            </>
          )}

          {isElectronicsListingCategory(formData.category) ? (
              <FormGroup>
                <Label>{t('listing.buyerContact')} *</Label>
                <p style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: 1.45, margin: '0.35rem 0 0.75rem' }}>
                  {t('listing.electronicsPhoneHint')}
                </p>
                <div style={{ padding: '0.85rem 1rem', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                  <p style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', color: '#334155' }}>
                    {t('listing.confirmPhoneNumber')}
                  </p>
                  <p style={{ margin: '0 0 0.65rem', fontWeight: 700, color: '#0f172a' }}>
                    {user?.phone_number || t('listing.noPhoneOnProfile')}
                  </p>
                  {(user?.phone_number || '').trim() ? (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={phoneDiscoverConfirm}
                        onChange={(e) => setPhoneDiscoverConfirm(e.target.checked)}
                        style={{ width: 16, height: 16 }}
                      />
                      {t('listing.showNumberOnListing')}
                    </label>
                  ) : (
                    <ErrorText>{t('listing.addPhoneForElectronics')}</ErrorText>
                  )}
                </div>
                {errors.phone_discoverable && <ErrorText>{errors.phone_discoverable}</ErrorText>}
              </FormGroup>
          ) : null}

          {isBooksListingCategory(formData.category) && (
            <>
              <FormGroup>
                <Label htmlFor="book_type">{t('listing.bookType')} *</Label>
                <Select
                  id="book_type"
                  value={formData.book_type}
                  onChange={(e) => handleInputChange('book_type', e.target.value)}
                  error={errors.book_type}
                >
                  <option value="">{t('listing.selectBookType')}</option>
                  {BOOK_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Select>
                {errors.book_type && <ErrorText>{errors.book_type}</ErrorText>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="book_language">{t('listing.language')} *</Label>
                <Select
                  id="book_language"
                  value={formData.book_language}
                  onChange={(e) => handleInputChange('book_language', e.target.value)}
                  error={errors.book_language}
                >
                  <option value="">{t('listing.selectLanguage')}</option>
                  {BOOK_LANGUAGES.map((lang) => (
                    <option key={lang} value={lang}>{lang}</option>
                  ))}
                </Select>
                {errors.book_language && <ErrorText>{errors.book_language}</ErrorText>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="book_audience">{t('listing.audience')} *</Label>
                <Select
                  id="book_audience"
                  value={formData.book_audience}
                  onChange={(e) => handleInputChange('book_audience', e.target.value)}
                  error={errors.book_audience}
                >
                  <option value="">{t('listing.selectAudience')}</option>
                  {BOOK_AUDIENCES.map((audience) => (
                    <option key={audience} value={audience}>{audience}</option>
                  ))}
                </Select>
                {errors.book_audience && <ErrorText>{errors.book_audience}</ErrorText>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="book_section">{t('listing.subjectArea')} *</Label>
                <Select
                  id="book_section"
                  value={formData.book_section}
                  onChange={(e) => {
                    handleInputChange('book_section', e.target.value);
                    handleInputChange('book_group', '');
                    handleInputChange('book_topic', '');
                  }}
                  error={errors.book_section}
                >
                  <option value="">{t('listing.selectSubjectArea')}</option>
                  {BOOK_SECTIONS.map((section) => (
                    <option key={section} value={section}>{section}</option>
                  ))}
                </Select>
                {errors.book_section && <ErrorText>{errors.book_section}</ErrorText>}
              </FormGroup>

              {formData.book_section && (
                <FormGroup>
                  <Label htmlFor="book_group">{t('listing.subcategory')} *</Label>
                  <Select
                    id="book_group"
                    value={formData.book_group}
                    onChange={(e) => {
                      handleInputChange('book_group', e.target.value);
                      handleInputChange('book_topic', '');
                    }}
                    error={errors.book_group}
                  >
                    <option value="">{t('listing.selectSubcategoryShort')}</option>
                    {getBookGroups(formData.book_section).map((group) => (
                      <option key={group} value={group}>{group}</option>
                    ))}
                  </Select>
                  {errors.book_group && <ErrorText>{errors.book_group}</ErrorText>}
                </FormGroup>
              )}

              {formData.book_group && (
                <FormGroup>
                  <Label htmlFor="book_topic">{t('listing.topic')} *</Label>
                  <Select
                    id="book_topic"
                    value={formData.book_topic}
                    onChange={(e) => handleInputChange('book_topic', e.target.value)}
                    error={errors.book_topic}
                  >
                    <option value="">{t('listing.selectTopic')}</option>
                    {getBookTopics(formData.book_section, formData.book_group).map((topic) => (
                      <option key={topic} value={topic}>{topic}</option>
                    ))}
                  </Select>
                  {errors.book_topic && <ErrorText>{errors.book_topic}</ErrorText>}
                </FormGroup>
              )}
            </>
          )}

          <FormGroup>
            <BrandSelector
              value={brandSelection}
              onChange={(next) => {
                setBrandSelection(next);
                if (errors.brand) setErrors((prev) => ({ ...prev, brand: '' }));
              }}
              required={brandRequired}
              error={errors.brand}
            />
          </FormGroup>

          {isWeddingDressListing && (
            <>
              <FormGroup>
                <Label htmlFor="title">{t('listing.dressTitle')} *</Label>
                <Input
                  id="title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder={t('listing.dressTitlePlaceholder')}
                  maxLength={200}
                  error={errors.title}
                />
                {errors.title && <ErrorText>{errors.title}</ErrorText>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="description">{t('listing.description')} *</Label>
                <TextArea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder={t('listing.dressDescriptionPlaceholderNotes')}
                  rows={4}
                  error={errors.description}
                />
                {errors.description && <ErrorText>{errors.description}</ErrorText>}
              </FormGroup>

              <FormGroup>
                <Label>{t('listing.hijabiQuestion')} *</Label>
                <Select
                  value={formData.is_hijabi}
                  onChange={(e) => handleInputChange('is_hijabi', e.target.value)}
                  error={errors.is_hijabi}
                >
                  <option value="">{t('listing.select')}</option>
                  <option value="true">{t('listing.yesHijabi')}</option>
                  <option value="false">{t('listing.noNonHijabi')}</option>
                </Select>
                {errors.is_hijabi && <ErrorText>{errors.is_hijabi}</ErrorText>}
              </FormGroup>
            </>
          )}

          {!isStructuredWeddingFlow
            && formData.category
            && listingAttrs
            && (!showWeddingContext || isWeddingListing) && (
            <>
              <FormGroup>
                <Label>{t('listing.color')} *</Label>
                <AttributeHint>{t('listing.colorHint')}</AttributeHint>
                <Select
                  name="color"
                  value={formData.color}
                  onChange={(e) => handleInputChange('color', e.target.value)}
                  error={errors.color}
                >
                  <option value="">{t('listing.selectColor')}</option>
                  {listingAttrs.colors.map((colorOption) => (
                    <option key={colorOption} value={colorOption}>{colorOption}</option>
                  ))}
                </Select>
                {errors.color && <ErrorText>{errors.color}</ErrorText>}
              </FormGroup>

              {listingAttrs.requiresSize && listingAttrs.sizeProfile && (
                <FormGroup>
                  <Label>{listingAttrs.sizeProfile.label}(s) * — {t('listing.selectOneOrMore')}</Label>
                  <AttributeHint>{listingAttrs.sizeProfile.hint}</AttributeHint>
                  {listingAttrs.sizeProfile.groups.map((group) => (
                    <SizeGroupBlock key={group.label || 'default'}>
                      {group.label ? <SizeGroupLabel>{group.label}</SizeGroupLabel> : null}
                      <ChipGrid>
                        {group.sizes.map((sizeOption) => (
                          <ChipOption key={`${group.label}-${sizeOption}`} $active={selectedSizes.includes(sizeOption)}>
                            <input
                              type="checkbox"
                              checked={selectedSizes.includes(sizeOption)}
                              onChange={() => toggleSize(sizeOption)}
                            />
                            {sizeOption}
                          </ChipOption>
                        ))}
                      </ChipGrid>
                    </SizeGroupBlock>
                  ))}
                  {errors.sizes && <ErrorText>{errors.sizes}</ErrorText>}
                </FormGroup>
              )}
            </>
          )}

          {isWeddingDressListing && (
            <>
              <FormGroup>
                <Label htmlFor="silhouette">{t('listing.dressSilhouette')} *</Label>
                <Select id="silhouette" value={weddingDress.silhouette} onChange={(e) => updateWeddingDress('silhouette', e.target.value)} error={errors.dress_silhouette}>
                  <option value="">{t('listing.selectSilhouette')}</option>
                  {WEDDING_DRESS_OPTIONS.silhouette.map((v) => <option key={v} value={v}>{v}</option>)}
                </Select>
                {errors.dress_silhouette && <ErrorText>{errors.dress_silhouette}</ErrorText>}
              </FormGroup>
              <FormGroup>
                <Label htmlFor="neckline">{t('listing.neckline')} *</Label>
                <Select id="neckline" value={weddingDress.neckline} onChange={(e) => updateWeddingDress('neckline', e.target.value)} error={errors.dress_neckline}>
                  <option value="">{t('listing.selectNeckline')}</option>
                  {WEDDING_DRESS_OPTIONS.neckline.map((v) => <option key={v} value={v}>{v}</option>)}
                </Select>
                {errors.dress_neckline && <ErrorText>{errors.dress_neckline}</ErrorText>}
              </FormGroup>
              <FormGroup>
                <Label htmlFor="sleeves">{t('listing.sleeves')} *</Label>
                <Select id="sleeves" value={weddingDress.sleeves} onChange={(e) => updateWeddingDress('sleeves', e.target.value)} error={errors.dress_sleeves}>
                  <option value="">{t('listing.selectSleeves')}</option>
                  {WEDDING_DRESS_OPTIONS.sleeves.map((v) => <option key={v} value={v}>{v}</option>)}
                </Select>
                {errors.dress_sleeves && <ErrorText>{errors.dress_sleeves}</ErrorText>}
              </FormGroup>
              <FormGroup>
                <Label htmlFor="back_design">{t('listing.backDesign')} *</Label>
                <Select id="back_design" value={weddingDress.back_design} onChange={(e) => updateWeddingDress('back_design', e.target.value)} error={errors.dress_back_design}>
                  <option value="">{t('listing.selectBackDesign')}</option>
                  {WEDDING_DRESS_OPTIONS.back_design.map((v) => <option key={v} value={v}>{v}</option>)}
                </Select>
                {errors.dress_back_design && <ErrorText>{errors.dress_back_design}</ErrorText>}
              </FormGroup>
              <FormGroup>
                <Label htmlFor="train">{t('listing.train')} *</Label>
                <Select id="train" value={weddingDress.train} onChange={(e) => updateWeddingDress('train', e.target.value)} error={errors.dress_train}>
                  <option value="">{t('listing.selectTrain')}</option>
                  {WEDDING_DRESS_OPTIONS.train.map((v) => <option key={v} value={v}>{v}</option>)}
                </Select>
                {errors.dress_train && <ErrorText>{errors.dress_train}</ErrorText>}
              </FormGroup>
              <FormGroup>
                <Label htmlFor="vibe">{t('listing.styleVibe')} *</Label>
                <Select id="vibe" value={weddingDress.vibe} onChange={(e) => updateWeddingDress('vibe', e.target.value)} error={errors.dress_vibe}>
                  <option value="">{t('listing.selectStyleVibe')}</option>
                  {WEDDING_DRESS_OPTIONS.vibe.map((v) => <option key={v} value={v}>{v}</option>)}
                </Select>
                {errors.dress_vibe && <ErrorText>{errors.dress_vibe}</ErrorText>}
              </FormGroup>
              <FormGroup>
                <Label htmlFor="main_fabric">{t('listing.mainFabric')} *</Label>
                <Select id="main_fabric" value={weddingDress.main_fabric} onChange={(e) => updateWeddingDress('main_fabric', e.target.value)} error={errors.dress_main_fabric}>
                  <option value="">{t('listing.selectMainFabric')}</option>
                  {WEDDING_DRESS_OPTIONS.main_fabric.map((v) => <option key={v} value={v}>{v}</option>)}
                </Select>
                {errors.dress_main_fabric && <ErrorText>{errors.dress_main_fabric}</ErrorText>}
              </FormGroup>
              <FormGroup>
                <Label htmlFor="lining">{t('listing.lining')} *</Label>
                <Select id="lining" value={weddingDress.lining} onChange={(e) => updateWeddingDress('lining', e.target.value)}>
                  <option value="">{t('listing.selectLining')}</option>
                  {WEDDING_DRESS_OPTIONS.lining.map((v) => <option key={v} value={v}>{v}</option>)}
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>{t('listing.embellishments')} *</Label>
                <ChipGrid>
                  {WEDDING_DRESS_OPTIONS.embellishments.map((v) => (
                    <ChipOption key={v} $active={weddingDress.embellishments.includes(v)}>
                      <input type="checkbox" checked={weddingDress.embellishments.includes(v)} onChange={() => toggleWeddingDressMulti('embellishments', v)} />
                      {v}
                    </ChipOption>
                  ))}
                </ChipGrid>
                {errors.dress_embellishments && <ErrorText>{errors.dress_embellishments}</ErrorText>}
              </FormGroup>
              <FormGroup>
                <Label htmlFor="fabric_color">{t('listing.color')} *</Label>
                <Select id="fabric_color" value={weddingDress.fabric_color} onChange={(e) => updateWeddingDress('fabric_color', e.target.value)} error={errors.dress_fabric_color}>
                  <option value="">{t('listing.selectColorShort')}</option>
                  {dressColorOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                </Select>
                <ChipGrid>
                  {dressColorOptions.map((v) => (
                    <ChipOption key={`sw-${v}`} $active={weddingDress.fabric_color === v}>
                      <input
                        type="radio"
                        name="fabric_color_quick"
                        checked={weddingDress.fabric_color === v}
                        onChange={() => updateWeddingDress('fabric_color', v)}
                      />
                      <ColorDot $color={COLOR_SWATCH_MAP[v]} />
                      {v}
                    </ChipOption>
                  ))}
                </ChipGrid>
                {errors.dress_fabric_color && <ErrorText>{errors.dress_fabric_color}</ErrorText>}
              </FormGroup>
              <FormGroup><Label htmlFor="bust_cm">{t('listing.bust')} *</Label><Input id="bust_cm" type="text" inputMode="decimal" value={weddingDress.bust_cm} onChange={(e) => updateWeddingDress('bust_cm', e.target.value.replace(/[^0-9.]/g, ''))} error={errors.dress_bust_cm} />{errors.dress_bust_cm && <ErrorText>{errors.dress_bust_cm}</ErrorText>}</FormGroup>
              <FormGroup><Label htmlFor="waist_cm">{t('listing.waist')} *</Label><Input id="waist_cm" type="text" inputMode="decimal" value={weddingDress.waist_cm} onChange={(e) => updateWeddingDress('waist_cm', e.target.value.replace(/[^0-9.]/g, ''))} error={errors.dress_waist_cm} />{errors.dress_waist_cm && <ErrorText>{errors.dress_waist_cm}</ErrorText>}</FormGroup>
              <FormGroup><Label htmlFor="hips_cm">{t('listing.hips')} *</Label><Input id="hips_cm" type="text" inputMode="decimal" value={weddingDress.hips_cm} onChange={(e) => updateWeddingDress('hips_cm', e.target.value.replace(/[^0-9.]/g, ''))} error={errors.dress_hips_cm} />{errors.dress_hips_cm && <ErrorText>{errors.dress_hips_cm}</ErrorText>}</FormGroup>
              <FormGroup><Label htmlFor="worn_height_cm">{t('listing.heightWithHeels')} *</Label><Input id="worn_height_cm" type="text" inputMode="decimal" value={weddingDress.worn_height_cm} onChange={(e) => updateWeddingDress('worn_height_cm', e.target.value.replace(/[^0-9.]/g, ''))} error={errors.dress_worn_height_cm} />{errors.dress_worn_height_cm && <ErrorText>{errors.dress_worn_height_cm}</ErrorText>}</FormGroup>
              <FormGroup><Label htmlFor="weight_range_kg">{t('listing.weightRange')} *</Label><Input id="weight_range_kg" type="text" value={weddingDress.weight_range_kg} onChange={(e) => updateWeddingDress('weight_range_kg', e.target.value)} placeholder={t('listing.weightRangePlaceholder')} error={errors.dress_weight_range_kg} />{errors.dress_weight_range_kg && <ErrorText>{errors.dress_weight_range_kg}</ErrorText>}</FormGroup>
              <FormGroup>
                <Label htmlFor="size_label">{t('listing.sizeLabel')} *</Label>
                <Select id="size_label" value={weddingDress.size_label} onChange={(e) => updateWeddingDress('size_label', e.target.value)} error={errors.dress_size_label}>
                  <option value="">{t('listing.selectSizeLabel')}</option>
                  {WEDDING_DRESS_OPTIONS.size_label.map((v) => <option key={v} value={v}>{v}</option>)}
                </Select>
                {errors.dress_size_label && <ErrorText>{errors.dress_size_label}</ErrorText>}
              </FormGroup>
              <FormGroup>
                <Label htmlFor="adjustability">{t('listing.adjustableFeatures')} *</Label>
                <Select id="adjustability" value={weddingDress.adjustability} onChange={(e) => updateWeddingDress('adjustability', e.target.value)} error={errors.dress_adjustability}>
                  <option value="">{t('listing.selectAdjustability')}</option>
                  {WEDDING_DRESS_OPTIONS.adjustability.map((v) => <option key={v} value={v}>{v}</option>)}
                </Select>
                {errors.dress_adjustability && <ErrorText>{errors.dress_adjustability}</ErrorText>}
              </FormGroup>
              <FormGroup>
                <Label htmlFor="weight_class">{t('listing.dressWeight')} *</Label>
                <Select id="weight_class" value={weddingDress.weight_class} onChange={(e) => updateWeddingDress('weight_class', e.target.value)} error={errors.dress_weight_class}>
                  <option value="">{t('listing.selectWeight')}</option>
                  {WEDDING_DRESS_OPTIONS.weight_class.map((v) => <option key={v} value={v}>{v}</option>)}
                </Select>
                {errors.dress_weight_class && <ErrorText>{errors.dress_weight_class}</ErrorText>}
              </FormGroup>
              <FormGroup>
                <Label htmlFor="mobility">{t('listing.easyToMove')} *</Label>
                <Select id="mobility" value={weddingDress.mobility} onChange={(e) => updateWeddingDress('mobility', e.target.value)} error={errors.dress_mobility}>
                  <option value="">{t('listing.selectMobility')}</option>
                  {WEDDING_DRESS_OPTIONS.mobility.map((v) => <option key={v} value={v}>{v}</option>)}
                </Select>
                {errors.dress_mobility && <ErrorText>{errors.dress_mobility}</ErrorText>}
              </FormGroup>
              <FormGroup>
                <Label htmlFor="danceable">{t('listing.suitableForDancing')} *</Label>
                <Select id="danceable" value={weddingDress.danceable} onChange={(e) => updateWeddingDress('danceable', e.target.value)} error={errors.dress_danceable}>
                  <option value="">{t('listing.selectOption')}</option>
                  {WEDDING_DRESS_OPTIONS.danceable.map((v) => <option key={v} value={v}>{v}</option>)}
                </Select>
                {errors.dress_danceable && <ErrorText>{errors.dress_danceable}</ErrorText>}
              </FormGroup>
              <FormGroup><Label htmlFor="flaws">{t('listing.flawsQuestion')}</Label><TextArea id="flaws" value={weddingDress.flaws} onChange={(e) => updateWeddingDress('flaws', e.target.value)} rows={3} /></FormGroup>
              <FormGroup><Label htmlFor="sale_price">{t('listing.salePrice')} *</Label><Input id="sale_price" type="text" inputMode="decimal" dir="ltr" value={weddingDress.sale_price} onChange={(e) => updateWeddingDress('sale_price', sanitizeDecimalInput(e.target.value))} error={errors.dress_sale_price} />{errors.dress_sale_price && <ErrorText>{errors.dress_sale_price}</ErrorText>}</FormGroup>
              <FormGroup>
                <Label>{t('listing.accessoriesIncluded')} *</Label>
                <ChipGrid>
                  {WEDDING_DRESS_OPTIONS.accessories.map((v) => (
                    <ChipOption key={v} $active={weddingDress.extras.includes(v)}>
                      <input type="checkbox" checked={weddingDress.extras.includes(v)} onChange={() => toggleWeddingDressMulti('extras', v)} />
                      {v}
                    </ChipOption>
                  ))}
                </ChipGrid>
                {errors.dress_extras && <ErrorText>{errors.dress_extras}</ErrorText>}
              </FormGroup>
            </>
          )}
          {isGroomStructuredListing && (
            <>
              <FormGroup>
                <Label htmlFor="title">{t('listing.suitTitle')} *</Label>
                <Input id="title" type="text" value={formData.title} onChange={(e) => handleInputChange('title', e.target.value)} placeholder={t('listing.suitTitlePlaceholder')} maxLength={200} error={errors.title} />
                {errors.title && <ErrorText>{errors.title}</ErrorText>}
              </FormGroup>
              <FormGroup>
                <Label htmlFor="description">{t('listing.description')} *</Label>
                <TextArea id="description" value={formData.description} onChange={(e) => handleInputChange('description', e.target.value)} placeholder={t('listing.groomDescriptionPlaceholder')} rows={4} error={errors.description} />
                {errors.description && <ErrorText>{errors.description}</ErrorText>}
              </FormGroup>
              {([
                ['suit_type', 'listing.suitType', 'listing.selectSuitType', GROOM_SUIT_OPTIONS.suit_type],
                ['fit', 'listing.fit', 'listing.selectFit', GROOM_SUIT_OPTIONS.fit],
                ['lapel', 'listing.lapel', 'listing.selectLapel', GROOM_SUIT_OPTIONS.lapel],
                ['closure', 'listing.closure', 'listing.selectClosure', GROOM_SUIT_OPTIONS.closure],
                ['pant_style', 'listing.pantStyle', 'listing.selectPantStyle', GROOM_SUIT_OPTIONS.pant_style],
                ['vibe', 'listing.styleVibe', 'listing.selectOverallStyleVibe', GROOM_SUIT_OPTIONS.vibe],
                ['main_fabric', 'listing.mainFabric', 'listing.selectMainFabric', GROOM_SUIT_OPTIONS.main_fabric],
                ['pattern', 'listing.pattern', 'listing.selectPattern', GROOM_SUIT_OPTIONS.pattern],
                ['color', 'listing.color', 'listing.selectColorShort', GROOM_SUIT_OPTIONS.color],
                ['size_label', 'listing.sizeLabel', 'listing.selectSizeLabel', GROOM_SUIT_OPTIONS.size_label],
                ['adjustability', 'listing.adjustability', 'listing.selectAdjustability', GROOM_SUIT_OPTIONS.adjustability],
                ['weight_class', 'listing.outfitWeight', 'listing.selectOutfitWeight', GROOM_SUIT_OPTIONS.weight_class],
                ['mobility', 'listing.easyToMove', 'listing.selectEasyToMove', GROOM_SUIT_OPTIONS.mobility],
                ['danceable', 'listing.suitableForDancing', 'listing.selectSuitableForDancing', GROOM_SUIT_OPTIONS.danceable],
              ]).map(([field, labelKey, selectKey, options]) => (
                <FormGroup key={field}>
                  <Label htmlFor={`groom_${field}`}>{t(labelKey)} *</Label>
                  <Select id={`groom_${field}`} value={groomSuit[field]} onChange={(e) => updateGroomSuit(field, e.target.value)} error={errors[`groom_${field}`]}>
                    <option value="">{t(selectKey)}</option>
                    {options.map((v) => <option key={v} value={v}>{v}</option>)}
                  </Select>
                  {errors[`groom_${field}`] && <ErrorText>{errors[`groom_${field}`]}</ErrorText>}
                </FormGroup>
              ))}
              <FormGroup>
                <Label>{t('listing.measurements')} *</Label>
                <Input type="text" inputMode="decimal" value={groomSuit.chest_cm} onChange={(e) => updateGroomSuit('chest_cm', e.target.value.replace(/[^0-9.]/g, ''))} placeholder={t('listing.chest')} error={errors.groom_chest_cm} />
                <Input type="text" inputMode="decimal" value={groomSuit.waist_cm} onChange={(e) => updateGroomSuit('waist_cm', e.target.value.replace(/[^0-9.]/g, ''))} placeholder={t('listing.waist')} error={errors.groom_waist_cm} />
                <Input type="text" inputMode="decimal" value={groomSuit.shoulder_cm} onChange={(e) => updateGroomSuit('shoulder_cm', e.target.value.replace(/[^0-9.]/g, ''))} placeholder={t('listing.shoulder')} error={errors.groom_shoulder_cm} />
                <Input type="text" inputMode="decimal" value={groomSuit.sleeve_cm} onChange={(e) => updateGroomSuit('sleeve_cm', e.target.value.replace(/[^0-9.]/g, ''))} placeholder={t('listing.sleeve')} error={errors.groom_sleeve_cm} />
                <Input type="text" inputMode="decimal" value={groomSuit.trouser_waist_in} onChange={(e) => updateGroomSuit('trouser_waist_in', e.target.value.replace(/[^0-9.]/g, ''))} placeholder={t('listing.trouserWaist')} error={errors.groom_trouser_waist_in} />
                <Input type="text" inputMode="decimal" value={groomSuit.trouser_length_in} onChange={(e) => updateGroomSuit('trouser_length_in', e.target.value.replace(/[^0-9.]/g, ''))} placeholder={t('listing.trouserLength')} error={errors.groom_trouser_length_in} />
                <Input type="text" inputMode="decimal" value={groomSuit.height_cm} onChange={(e) => updateGroomSuit('height_cm', e.target.value.replace(/[^0-9.]/g, ''))} placeholder={t('listing.height')} error={errors.groom_height_cm} />
                <Input type="text" value={groomSuit.weight_range_kg} onChange={(e) => updateGroomSuit('weight_range_kg', e.target.value)} placeholder={t('listing.weightRangeSuit')} error={errors.groom_weight_range_kg} />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="groom_flaws">{t('listing.flawsQuestion')}</Label>
                <TextArea id="groom_flaws" value={groomSuit.flaws} onChange={(e) => updateGroomSuit('flaws', e.target.value)} rows={3} />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="groom_sale_price">{t('listing.salePrice')} *</Label>
                <Input id="groom_sale_price" type="text" inputMode="decimal" dir="ltr" value={groomSuit.sale_price} onChange={(e) => updateGroomSuit('sale_price', sanitizeDecimalInput(e.target.value))} error={errors.groom_sale_price} />
                {errors.groom_sale_price && <ErrorText>{errors.groom_sale_price}</ErrorText>}
              </FormGroup>
              <FormGroup>
                <Label>{t('listing.includedItems')} *</Label>
                <ChipGrid>
                  {GROOM_SUIT_OPTIONS.included_items.map((v) => (
                    <ChipOption key={v} $active={groomSuit.included_items.includes(v)}>
                      <input type="checkbox" checked={groomSuit.included_items.includes(v)} onChange={() => toggleGroomSuitMulti('included_items', v)} />
                      {v}
                    </ChipOption>
                  ))}
                </ChipGrid>
                {errors.groom_included_items && <ErrorText>{errors.groom_included_items}</ErrorText>}
              </FormGroup>
            </>
          )}

          <FormGroup>
            <Label htmlFor="condition">{t('listing.condition')} *</Label>
            <Select
              id="condition"
              value={formData.condition}
              onChange={(e) => handleInputChange('condition', e.target.value)}
              error={errors.condition}
            >
              <option value="">{t('listing.selectCondition')}</option>
              {conditions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </Select>
            {errors.condition && <ErrorText>{errors.condition}</ErrorText>}
          </FormGroup>

          {!isStructuredWeddingFlow && (
            <>
              <FormGroup>
                <Label>
                  <input
                    type="checkbox"
                    checked={formData.fragile}
                    onChange={(e) => handleInputChange('fragile', e.target.checked)}
                    style={{
                      marginRight: '0.5rem',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      verticalAlign: 'middle',
                    }}
                  />
                  {t('listing.fragileLabel')}
                </Label>

              </FormGroup>
            </>
          )}

          {/* Weight Checkbox (Electronics) */}
          {isElectronicsListingCategory(formData.category) && (
            <FormGroup>
              <Label>
                <input
                  type="checkbox"
                  checked={formData.weight_over_15kg}
                  onChange={(e) => handleInputChange('weight_over_15kg', e.target.checked)}
                  style={{
                    marginRight: '0.5rem',
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                  }}
                />
                {t('listing.weightOver15')}
              </Label>
            </FormGroup>
          )}

          {showDefectedQuestion && (
            <FormGroup>
              <InlineChoiceRow>
                <Label style={{ marginBottom: 0 }}>{t('listing.defectedQuestion')}</Label>
                <InlineChoiceOptions>
                  <RadioOption $active={formData.is_defected === 'true'}>
                    <input
                      type="radio"
                      name="is_defected"
                      value="true"
                      checked={formData.is_defected === 'true'}
                      onChange={(e) => handleInputChange('is_defected', e.target.value)}
                    />
                    {t('listing.yes')}
                  </RadioOption>
                  <RadioOption $active={formData.is_defected === 'false'}>
                    <input
                      type="radio"
                      name="is_defected"
                      value="false"
                      checked={formData.is_defected === 'false'}
                      onChange={(e) => handleInputChange('is_defected', e.target.value)}
                    />
                    {t('listing.no')}
                  </RadioOption>
                </InlineChoiceOptions>
              </InlineChoiceRow>
            </FormGroup>
          )}

          <ListingItemFlawsField value={itemFlaws} onChange={setItemFlaws} />

          <ButtonGroup>
            <PrimaryButton type="submit" disabled={loading || uploading || savingAddress}>
              {waitingUploads
                ? t('listing.finishingUploads')
                : loading || uploading || savingAddress
                  ? t('listing.creatingItem')
                  : t('listing.offerItem')}
            </PrimaryButton>
            <SecondaryButton
              type="button"
              onClick={() => navigate(exitPath)}
            >
              {t('common.cancel')}
            </SecondaryButton>
          </ButtonGroup>
        </Form>
      </PageInner>

      {showAddressStep && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 9999, padding: '1rem',
        }}>
          <div style={{
            backgroundColor: 'var(--bg-primary, #fff)', borderRadius: '0.75rem',
            padding: '2rem', maxWidth: '520px', width: '100%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto',
          }}>
            <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)' }}>
              {t('listing.confirmLocation')}
            </h2>
            <p style={{ margin: '0 0 1.25rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              {t('listing.confirmAddressBody')}
            </p>

            {shippingAddress.trim() ? (
              <div style={{
                padding: '1rem', borderRadius: '0.5rem',
                backgroundColor: 'var(--bg-secondary, #f3f4f6)', marginBottom: '1rem',
                border: '1px solid var(--border-color, #e5e7eb)',
              }}>
                <p style={{ margin: 0, fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                  {t('listing.savedAddress')}
                </p>
                <p style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)' }}>
                  {shippingAddress}
                </p>
              </div>
            ) : (
              <p style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: '#dc3545', fontWeight: 500 }}>
                {t('listing.noAddressSaved')}
              </p>
            )}

            <LocationPicker
              value={shippingAddress}
              onChange={(address, coords) => {
                setShippingAddress(address);
                if (coords?.latitude != null && coords?.longitude != null) {
                  setAddressCoords({ latitude: coords.latitude, longitude: coords.longitude });
                }
              }}
            />

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
              <PrimaryButton
                type="button"
                onClick={handleConfirmAndSubmit}
                disabled={savingAddress || uploading || !shippingAddress.trim()}
                style={{ flex: 1 }}
              >
                {waitingUploads
                  ? t('listing.finishingUploads')
                  : savingAddress || uploading
                    ? t('listing.submitting')
                    : t('listing.confirmOffer')}
              </PrimaryButton>
              <SecondaryButton
                type="button"
                onClick={() => setShowAddressStep(false)}
                style={{ flex: 0 }}
              >
                {t('common.back')}
              </SecondaryButton>
            </div>
          </div>
        </div>
      )}
    </PageWrapper>
  );
};

export default CreateItemPage;
